import axios from "axios";
import { spotViewerAuthorizationHeader } from "../constants/spotViewerAuth";
import {
  buildExporterQueryParams,
  executeExporter,
  exporterCsvToRecords,
  fetchExporterDefinition,
  parseExporterCsv,
  resolveExporterQueryType,
} from "../exporter/exporterService";
import { uploadFile, downloadFile, fetchFileDownloadUrl } from "../files/filesService";
import { httpClient } from "../http/client";
import { sortSpotRoundsBySequenceAsc } from "../spot/spotRoundSort";

export interface ApplicantCollege {
  collegeId: number;
  /** College/institute code used by Spot Super Admin onboard path. */
  code: string;
  name: string;
  preferred: boolean;
  homeUniversity?: string;
  type?: string;
  city?: string;
  stateCode?: string;
  courses?: string[];
  nirfRank?: number;
  apiDetails?: Array<{ label: string; value: string }>;
}

interface RawApplicantCollege {
  collegeId?: unknown;
  college_id?: unknown;
  collegeID?: unknown;
  id?: unknown;
  collegeCode?: unknown;
  college_code?: unknown;
  code?: unknown;
  name?: unknown;
  title?: unknown;
  label?: unknown;
  collegeName?: unknown;
  college_name?: unknown;
  college?: unknown;
  collegeDetails?: unknown;
  collegeInfo?: unknown;
  college_info?: unknown;
  institute?: unknown;
  homeUniversity?: unknown;
  home_university?: unknown;
  university?: unknown;
  universityName?: unknown;
  university_name?: unknown;
  preferred?: unknown;
  type?: unknown;
  collegeType?: unknown;
  college_type?: unknown;
  city?: unknown;
  City?: unknown;
  state?: unknown;
  State?: unknown;
  stateCode?: unknown;
  state_code?: unknown;
  StateCode?: unknown;
  courses?: unknown;
  courseNames?: unknown;
  course_names?: unknown;
  location?: unknown;
  nirfRank?: unknown;
  nirf_rank?: unknown;
  rank?: unknown;
}

interface ParsedCollegesPage {
  content: ApplicantCollege[];
  last: boolean;
}

const DEFAULT_PAGE_SIZE = 1000;
const EXCLUDED_DETAIL_KEYS = new Set([
  "collegeId",
  "college_id",
  "collegeID",
  "id",
  "collegeCode",
  "college_code",
  "name",
  "title",
  "label",
  "collegeName",
  "college_name",
  "college",
  "collegeDetails",
  "institute",
  "preferred",
  "type",
  "collegeType",
  "college_type",
  "city",
  "state",
  "stateCode",
  "state_code",
  "courses",
  "courseNames",
  "course_names",
  "location",
  "nirfRank",
  "nirf_rank",
  "rank",
]);

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function toStringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** Coerce round API date fields (string, epoch, or [y,m,d]) into a parseable string. */
function toRoundDateValue(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(ms);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString();
    }
  }
  if (Array.isArray(value) && value.length >= 3) {
    const year = Number(value[0]);
    const month = Number(value[1]);
    const day = Number(value[2]);
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }
  return "";
}

function pickRoundDateFromRecords(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string {
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const value = toRoundDateValue(record[key]);
      if (value) return value;
    }
  }
  return "";
}

function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
    if (normalized === "false" || normalized === "0" || normalized === "no") return false;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  return false;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    if (typeof value === "string") {
      return value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }
  return value
    .map((item) => {
      if (typeof item === "string") {
        return item.trim();
      }
      if (item && typeof item === "object") {
        const obj = item as Record<string, unknown>;
        return toStringValue(
          obj.courseName
          ?? obj.course_name
          ?? obj.name
          ?? obj.title
          ?? obj.label
          ?? obj.value,
        );
      }
      return "";
    })
    .filter(Boolean);
}

function toDisplayLabel(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toDetailString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const items = value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (typeof item === "number" && Number.isFinite(item)) return String(item);
        if (typeof item === "boolean") return item ? "Yes" : "No";
        if (item && typeof item === "object") {
          const obj = item as Record<string, unknown>;
          return toStringValue(obj.name ?? obj.label ?? obj.title ?? obj.value ?? obj.courseName ?? obj.course_name);
        }
        return "";
      })
      .filter(Boolean);
    return items.join(", ");
  }
  return "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function normalizeLookupKey(key: string): string {
  // Case-insensitive; treats spaces, underscores, camelCase separators, and punctuation the same.
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function coerceToDisplayString(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return "";
}

/** Stable positive 31-bit id for alphanumeric exporter codes that are not pure numbers. */
function stableStringHash(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  const positive = hash === 0 ? 1 : Math.abs(hash);
  return positive % 2147483647 || 1;
}

function coerceToCollegeId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === "boolean") {
    return null;
  }
  if (typeof value === "string" && value.trim()) {
    const trimmed = value.trim();
    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber)) {
      return Math.trunc(asNumber);
    }
    const digits = trimmed.replace(/\D+/g, "");
    if (digits) {
      const fromDigits = Number(digits);
      if (Number.isFinite(fromDigits)) {
        return Math.trunc(fromDigits);
      }
    }
    return stableStringHash(trimmed);
  }
  return null;
}

interface ResolvedField {
  key: string;
  value: unknown;
  score: number;
}

function scoreCollegeIdField(normalizedKey: string): number {
  if (!normalizedKey) {
    return 0;
  }

  // Never treat college/institute *code* fields as database IDs.
  if (
    normalizedKey === "collegecode"
    || normalizedKey === "institutecode"
    || normalizedKey.endsWith("collegecode")
    || normalizedKey.endsWith("institutecode")
    || normalizedKey === "code"
    || normalizedKey === "cd"
    || (
      (normalizedKey.includes("college") || normalizedKey.includes("institute") || normalizedKey.includes("clg"))
      && (normalizedKey.endsWith("code") || normalizedKey.endsWith("cd"))
      && !normalizedKey.endsWith("id")
    )
  ) {
    return 0;
  }

  // Avoid mistaking name/location fields for ids.
  if (
    /(name|city|state|type|address|email|phone|mobile|rank|course|university)/.test(normalizedKey)
    && !/id$/.test(normalizedKey)
  ) {
    return 0;
  }

  if (normalizedKey === "collegeid" || normalizedKey === "instituteid") {
    return 100;
  }
  if (normalizedKey.endsWith("collegeid") || normalizedKey.endsWith("instituteid")) {
    return 95;
  }
  if (
    (normalizedKey.includes("college") || normalizedKey.includes("institute") || normalizedKey.includes("clg"))
    && normalizedKey.endsWith("id")
  ) {
    return 90;
  }
  if (normalizedKey === "id") {
    return 80;
  }
  if (normalizedKey.endsWith("id")) {
    return 35;
  }
  return 0;
}

/** Prefer explicit college/institute code fields for onboard path parameters. */
function scoreCollegeCodeField(normalizedKey: string): number {
  if (!normalizedKey) {
    return 0;
  }

  // Avoid state/pin/postal/country codes and name fields.
  if (/(state|pin|postal|zip|country|name|email|phone|mobile|rank|type|city|address|university)/.test(normalizedKey)) {
    return 0;
  }

  if (normalizedKey === "collegecode" || normalizedKey === "institutecode") {
    return 100;
  }
  if (normalizedKey.endsWith("collegecode") || normalizedKey.endsWith("institutecode")) {
    return 95;
  }
  if (
    (normalizedKey.includes("college") || normalizedKey.includes("institute") || normalizedKey.includes("clg"))
    && (normalizedKey.endsWith("code") || normalizedKey.endsWith("cd"))
  ) {
    return 90;
  }
  if (normalizedKey === "code" || normalizedKey === "cd") {
    return 70;
  }
  if (normalizedKey.endsWith("code") || normalizedKey.endsWith("cd")) {
    return 40;
  }
  return 0;
}

function coerceToCollegeCode(value: unknown): string {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return "";
}

function scoreCollegeNameField(normalizedKey: string): number {
  if (!normalizedKey) {
    return 0;
  }

  if (
    /(id|code|cd|email|phone|mobile|rank|type|city|state|address)$/.test(normalizedKey)
    && !normalizedKey.includes("name")
  ) {
    return 0;
  }

  if (normalizedKey === "collegename" || normalizedKey === "institutename") {
    return 100;
  }
  if (
    (normalizedKey.includes("college") || normalizedKey.includes("institute") || normalizedKey.includes("clg"))
    && normalizedKey.includes("name")
  ) {
    return 95;
  }
  if (normalizedKey === "name" || normalizedKey === "title" || normalizedKey === "label") {
    return 70;
  }
  if (normalizedKey === "college" || normalizedKey === "institute") {
    return 50;
  }
  if (normalizedKey.includes("name")) {
    return 40;
  }
  return 0;
}

function resolveBestField(
  record: Record<string, unknown>,
  scoreKey: (normalizedKey: string) => number,
): ResolvedField | null {
  let best: ResolvedField | null = null;
  for (const [key, value] of Object.entries(record)) {
    const score = scoreKey(normalizeLookupKey(key));
    if (score <= 0) {
      continue;
    }
    if (!best || score > best.score) {
      best = { key, value, score };
    }
  }
  return best;
}

function pickCollegeId(record: Record<string, unknown>): number | null {
  const resolved = resolveBestField(record, scoreCollegeIdField);
  return resolved ? coerceToCollegeId(resolved.value) : null;
}

function pickCollegeName(record: Record<string, unknown>): string {
  const resolved = resolveBestField(record, scoreCollegeNameField);
  return resolved ? coerceToDisplayString(resolved.value) : "";
}

function detectIdentityColumns(record: Record<string, unknown>): {
  columns: string[];
  idColumn: string | null;
  nameColumn: string | null;
} {
  const idField = resolveBestField(record, scoreCollegeIdField);
  const nameField = resolveBestField(record, scoreCollegeNameField);
  return {
    columns: Object.keys(record),
    idColumn: idField?.key ?? null,
    nameColumn: nameField?.key ?? null,
  };
}

function collectApiDetails(
  source: Record<string, unknown> | null,
  detailMap: Map<string, string>,
  prefix: string = "",
): void {
  if (!source) return;

  Object.entries(source).forEach(([key, value]) => {
    if (EXCLUDED_DETAIL_KEYS.has(key)) {
      return;
    }

    if (value && typeof value === "object" && !Array.isArray(value)) {
      collectApiDetails(value as Record<string, unknown>, detailMap, `${prefix}${toDisplayLabel(key)} `);
      return;
    }

    const text = toDetailString(value);
    if (!text) {
      return;
    }

    const label = `${prefix}${toDisplayLabel(key)}`.trim();
    if (!detailMap.has(label)) {
      detailMap.set(label, text);
    }
  });
}

function normalizeApplicantCollege(
  raw: unknown,
  options?: { logDiscard?: boolean; rowIndex?: number },
): ApplicantCollege | null {
  if (!raw || typeof raw !== "object") {
    if (options?.logDiscard) {
      console.info("[CollegeExporter][mapping] discard row", {
        rowIndex: options.rowIndex,
        reason: "row is not an object",
      });
    }
    return null;
  }

  const value = raw as RawApplicantCollege;
  const root = asRecord(value) ?? {};
  const nestedCollege = asRecord(value.college);
  const nestedDetails = asRecord(value.collegeDetails);
  const nestedCollegeInfo = asRecord(value.collegeInfo) ?? asRecord(value.college_info);
  const nestedInstitute = asRecord(value.institute);
  const location = asRecord(value.location);
  const candidates = [root, nestedCollege, nestedDetails, nestedCollegeInfo, nestedInstitute].filter(
    (item): item is Record<string, unknown> => Boolean(item),
  );

  let collegeId: number | null = null;
  let name = "";
  let code = "";
  let idSourceKey: string | null = null;
  let nameSourceKey: string | null = null;

  // Prefer explicit database id fields when both id and code are present.
  const explicitId =
    coerceToCollegeId(value.collegeId)
    ?? coerceToCollegeId(value.college_id)
    ?? coerceToCollegeId(value.collegeID)
    ?? coerceToCollegeId(value.id)
    ?? coerceToCollegeId(nestedCollege?.collegeId)
    ?? coerceToCollegeId(nestedCollege?.college_id)
    ?? coerceToCollegeId(nestedCollege?.collegeID)
    ?? coerceToCollegeId(nestedCollege?.id)
    ?? coerceToCollegeId(nestedDetails?.collegeId)
    ?? coerceToCollegeId(nestedDetails?.college_id)
    ?? coerceToCollegeId(nestedDetails?.id)
    ?? coerceToCollegeId(nestedCollegeInfo?.collegeId)
    ?? coerceToCollegeId(nestedCollegeInfo?.college_id)
    ?? coerceToCollegeId(nestedCollegeInfo?.id)
    ?? coerceToCollegeId(nestedInstitute?.collegeId)
    ?? coerceToCollegeId(nestedInstitute?.college_id)
    ?? coerceToCollegeId(nestedInstitute?.id);
  if (explicitId !== null) {
    collegeId = explicitId;
    idSourceKey = "explicit-college-id";
  }

  for (const candidate of candidates) {
    if (collegeId === null) {
      const idField = resolveBestField(candidate, scoreCollegeIdField);
      if (idField) {
        collegeId = coerceToCollegeId(idField.value);
        idSourceKey = idField.key;
      }
    }
    if (!name) {
      const nameField = resolveBestField(candidate, scoreCollegeNameField);
      if (nameField) {
        name = coerceToDisplayString(nameField.value);
        nameSourceKey = nameField.key;
      }
    }
    if (!code) {
      const codeField = resolveBestField(candidate, scoreCollegeCodeField);
      if (codeField) {
        code = coerceToCollegeCode(codeField.value);
      }
    }
    if (collegeId !== null && name && code) {
      break;
    }
  }

  // Explicit code fields on the raw row (including nested shapes already scanned above).
  if (!code) {
    code = coerceToCollegeCode(
      value.collegeCode
      ?? value.college_code
      ?? value.code
      ?? nestedCollege?.collegeCode
      ?? nestedCollege?.college_code
      ?? nestedCollege?.code
      ?? nestedDetails?.collegeCode
      ?? nestedDetails?.college_code
      ?? nestedDetails?.code
      ?? nestedCollegeInfo?.collegeCode
      ?? nestedCollegeInfo?.college_code
      ?? nestedCollegeInfo?.code
      ?? nestedInstitute?.collegeCode
      ?? nestedInstitute?.college_code
      ?? nestedInstitute?.code,
    );
  }

  const type = toStringValue(
    value.type
    ?? value.collegeType
    ?? value.college_type
    ?? nestedCollege?.type
    ?? nestedDetails?.type
    ?? nestedCollegeInfo?.type
    ?? nestedInstitute?.type,
  );
  const city = toStringValue(
    value.city
    ?? value.City
    ?? location?.city
    ?? location?.City
    ?? nestedCollege?.city
    ?? nestedCollege?.City
    ?? nestedDetails?.city
    ?? nestedDetails?.City
    ?? nestedCollegeInfo?.city
    ?? nestedCollegeInfo?.City
    ?? nestedInstitute?.city
    ?? nestedInstitute?.City,
  );
  const stateCode = toStringValue(
    value.stateCode
    ?? value.state_code
    ?? value.StateCode
    ?? value.state
    ?? value.State
    ?? location?.stateCode
    ?? location?.state_code
    ?? location?.StateCode
    ?? location?.state
    ?? location?.State
    ?? nestedCollegeInfo?.stateCode
    ?? nestedCollegeInfo?.state_code
    ?? nestedCollegeInfo?.StateCode
    ?? nestedCollegeInfo?.state
    ?? nestedCollegeInfo?.State
  );
  const courses = toStringArray(
    value.courses
    ?? value.courseNames
    ?? value.course_names
    ?? nestedCollege?.courses
    ?? nestedDetails?.courses
    ?? nestedCollegeInfo?.courses
    ?? nestedCollegeInfo?.courseNames
    ?? nestedCollegeInfo?.course_names
    ?? nestedInstitute?.courses,
  );
  const nirfRank = toNumber(
    value.nirfRank
    ?? value.nirf_rank
    ?? value.rank
    ?? nestedCollege?.nirfRank
    ?? nestedDetails?.nirfRank
    ?? nestedCollegeInfo?.nirfRank
    ?? nestedCollegeInfo?.nirf_rank
    ?? nestedCollegeInfo?.rank
    ?? nestedInstitute?.nirfRank,
  );
  const preferred = toBoolean(
    value.preferred
    ?? nestedCollege?.preferred
    ?? nestedDetails?.preferred
    ?? nestedInstitute?.preferred,
  );

  // Only discard when the row truly lacks both identity fields.
  if (collegeId === null && !name) {
    if (options?.logDiscard) {
      console.info("[CollegeExporter][mapping] discard row", {
        rowIndex: options.rowIndex,
        reason: "missing both college ID and college name",
        columns: Object.keys(root),
        idSourceKey,
        nameSourceKey,
      });
    }
    return null;
  }

  if (collegeId === null) {
    collegeId = stableStringHash(name);
    if (options?.logDiscard) {
      console.info("[CollegeExporter][mapping] synthesized collegeId from name", {
        rowIndex: options.rowIndex,
        name,
        collegeId,
        nameSourceKey,
      });
    }
  }

  if (!name && collegeId !== null) {
    name = `College ${collegeId}`;
    if (options?.logDiscard) {
      console.info("[CollegeExporter][mapping] synthesized college name from id", {
        rowIndex: options.rowIndex,
        collegeId,
        name,
        idSourceKey,
      });
    }
  }

  // When the exporter only exposes one numeric identifier, reuse it as the college code
  // (this is the value previously sent in the onboard path).
  if (!code && collegeId !== null) {
    code = String(collegeId);
  }

  const homeUniversity = toStringValue(
    value.homeUniversity
    ?? value.home_university
    ?? value.university
    ?? value.universityName
    ?? value.university_name
    ?? nestedCollege?.homeUniversity
    ?? nestedCollege?.home_university
    ?? nestedCollege?.university
    ?? nestedCollege?.universityName
    ?? nestedCollege?.university_name
    ?? nestedDetails?.homeUniversity
    ?? nestedDetails?.home_university
    ?? nestedDetails?.university
    ?? nestedDetails?.universityName
    ?? nestedDetails?.university_name
    ?? nestedInstitute?.homeUniversity
    ?? nestedInstitute?.home_university
    ?? nestedInstitute?.university
    ?? nestedInstitute?.universityName
    ?? nestedInstitute?.university_name,
  );

  const detailMap = new Map<string, string>();
  collectApiDetails(root, detailMap);
  collectApiDetails(nestedCollege, detailMap);
  collectApiDetails(nestedDetails, detailMap);
  collectApiDetails(nestedCollegeInfo, detailMap);
  collectApiDetails(nestedInstitute, detailMap);

  const apiDetails = Array.from(detailMap.entries())
    .map(([label, detailValue]) => ({ label, value: detailValue }))
    .filter((detail) => detail.value !== name);

  if (options?.logDiscard) {
    console.info("[CollegeExporter][mapping] id vs code", {
      rowIndex: options.rowIndex,
      collegeId,
      code,
      idSourceKey,
      name,
    });
  }

  return {
    collegeId,
    code,
    name,
    preferred,
    homeUniversity: homeUniversity || undefined,
    type: type || undefined,
    city: city || undefined,
    stateCode: stateCode || undefined,
    courses: courses.length > 0 ? courses : undefined,
    nirfRank: nirfRank ?? undefined,
    apiDetails: apiDetails.length > 0 ? apiDetails : undefined,
  };
}

function pickItemsContainer(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const root = raw as Record<string, unknown>;
  if (Array.isArray(root.content) || Array.isArray(root.items) || Array.isArray(root.colleges)) {
    return root;
  }

  const nestedData = root.data;
  if (nestedData && typeof nestedData === "object") {
    const candidate = nestedData as Record<string, unknown>;
    if (Array.isArray(candidate.content) || Array.isArray(candidate.items) || Array.isArray(candidate.colleges)) {
      return candidate;
    }
  }

  const nestedResult = root.result;
  if (nestedResult && typeof nestedResult === "object") {
    const candidate = nestedResult as Record<string, unknown>;
    if (Array.isArray(candidate.content) || Array.isArray(candidate.items) || Array.isArray(candidate.colleges)) {
      return candidate;
    }
  }

  return root;
}

function parseApplicantCollegesPage(raw: unknown, size: number): ParsedCollegesPage {
  const container = pickItemsContainer(raw);
  const rawItems = Array.isArray(raw)
    ? raw
    : container
      ? (Array.isArray(container.content)
        ? container.content
        : Array.isArray(container.items)
          ? container.items
          : Array.isArray(container.colleges)
            ? container.colleges
            : [])
      : [];

  const content = rawItems
    .map((item) => normalizeApplicantCollege(item))
    .filter((item): item is ApplicantCollege => item !== null);

  if (!container || Array.isArray(raw)) {
    return { content, last: true };
  }

  const hasPaginationMetadata =
    Object.prototype.hasOwnProperty.call(container, "last")
    || Object.prototype.hasOwnProperty.call(container, "number")
    || Object.prototype.hasOwnProperty.call(container, "totalPages")
    || Object.prototype.hasOwnProperty.call(container, "total_pages")
    || Object.prototype.hasOwnProperty.call(container, "page");

  const explicitLast = typeof container.last === "boolean" ? container.last : null;
  if (explicitLast !== null) {
    return { content, last: explicitLast };
  }

  if (!hasPaginationMetadata) {
    return { content, last: true };
  }

  const pageNumber = toNumber(container.number ?? container.page ?? 0) ?? 0;
  const totalPages = toNumber(container.totalPages ?? container.total_pages);
  if (typeof totalPages === "number") {
    return { content, last: pageNumber >= totalPages - 1 };
  }

  return { content, last: content.length < size };
}

async function fetchApplicantCollegesPage(page: number, size: number): Promise<ParsedCollegesPage> {
  const response = await httpClient.get<unknown>("/api/v1/applicants/me/colleges", {
    headers: {
      Accept: "application/json",
    },
    params: {
      page,
      size,
    },
  });
  return parseApplicantCollegesPage(response.data, size);
}

let fetchAllApplicantCollegesPromise: Promise<ApplicantCollege[]> | null = null;

export async function fetchAllApplicantColleges(pageSize: number = DEFAULT_PAGE_SIZE): Promise<ApplicantCollege[]> {
  if (fetchAllApplicantCollegesPromise) {
    return fetchAllApplicantCollegesPromise;
  }

  fetchAllApplicantCollegesPromise = (async () => {
    try {
      let page = 0;
      let isLastPage = false;
      const colleges: ApplicantCollege[] = [];
      const seenIds = new Set<number>();
      const MAX_PAGES = 50;

      while (!isLastPage && page < MAX_PAGES) {
        const data = await fetchApplicantCollegesPage(page, pageSize);
        data.content.forEach((college) => {
          if (!seenIds.has(college.collegeId)) {
            seenIds.add(college.collegeId);
            colleges.push(college);
          }
        });
        isLastPage = data.last;
        page += 1;
      }

      return colleges;
    } finally {
      fetchAllApplicantCollegesPromise = null;
    }
  })();

  return fetchAllApplicantCollegesPromise;
}

const COLLEGE_EXPORTER_NAME = "CollegeExporter";

function throwIfExporterJsonLooksLikeError(payload: unknown, requestUrl: string): void {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return;
  }

  const item = payload as Record<string, unknown>;
  const message =
    (typeof item.message === "string" && item.message.trim())
    || (typeof item.error === "string" && item.error.trim())
    || (typeof item.detail === "string" && item.detail.trim())
    || null;

  if (!message && item.status == null && item.error == null) {
    return;
  }

  throw new Error(
    `CollegeExporter export returned JSON without college rows `
    + `(POST ${requestUrl}): ${message ?? "unexpected JSON payload"}. `
    + "Verify the export endpoint and request body format.",
  );
}

/**
 * Extracts a college list from flexible CollegeExporter / admin-exporter payloads.
 * Supports a raw array or common wrapper keys used by exporter APIs.
 */
function extractExporterCollegeItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) {
    return raw;
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) {
      return [];
    }
    try {
      return extractExporterCollegeItems(JSON.parse(trimmed) as unknown);
    } catch {
      return [];
    }
  }

  if (!raw || typeof raw !== "object") {
    return [];
  }

  const root = raw as Record<string, unknown>;
  const directKeys = [
    "content",
    "items",
    "colleges",
    "data",
    "rows",
    "records",
    "list",
    "result",
    "exportData",
    "export_data",
  ] as const;

  for (const key of directKeys) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value;
    }
  }

  for (const key of directKeys) {
    const value = root[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const nested = extractExporterCollegeItems(value);
      if (nested.length > 0) {
        return nested;
      }
    }
  }

  return [];
}

/**
 * Maps CollegeExporter rows onto the existing Super Admin dropdown model (`ApplicantCollege`).
 */
function mapCollegeExporterToApplicantColleges(raw: unknown): ApplicantCollege[] {
  const seenIds = new Set<number>();
  const colleges: ApplicantCollege[] = [];
  const items = extractExporterCollegeItems(raw);

  if (items.length > 0) {
    const firstRecord = asRecord(items[0]);
    if (firstRecord) {
      const detected = detectIdentityColumns(firstRecord);
      console.info("[CollegeExporter][mapping] columns detected", {
        columns: detected.columns,
        idColumn: detected.idColumn,
        nameColumn: detected.nameColumn,
        sampleRowCount: items.length,
      });
    } else {
      console.info("[CollegeExporter][mapping] columns detected", {
        columns: [],
        reason: "first row is not an object",
        sampleRowCount: items.length,
      });
    }
  } else {
    console.info("[CollegeExporter][mapping] columns detected", {
      columns: [],
      reason: "no exporter rows to map",
    });
  }

  let discarded = 0;
  for (let index = 0; index < items.length; index += 1) {
    const college = normalizeApplicantCollege(items[index], {
      logDiscard: discarded < 5 || index < 3,
      rowIndex: index,
    });
    if (!college) {
      discarded += 1;
      continue;
    }
    if (seenIds.has(college.collegeId)) {
      continue;
    }
    seenIds.add(college.collegeId);
    colleges.push({
      ...college,
      // Exporter catalog is not an applicant shortlist; keep preferred false.
      preferred: false,
    });
  }

  console.info("[CollegeExporter][mapping] result", {
    inputRows: items.length,
    mappedColleges: colleges.length,
    discardedRows: discarded,
  });

  return colleges;
}

let fetchCollegeExporterCollegesPromise: Promise<ApplicantCollege[]> | null = null;

/**
 * Loads the college catalog for Super Admin onboarding from the CollegeExporter API.
 *
 * Sequence:
 * 1. GET /admin/exporter/get/CollegeExporter — exporter definition
 * 2. Resolve query type from definition (prefer "All" when listed)
 * 3. POST /admin/exporter/export/CollegeExporter/{queryType} — export data (CSV or JSON)
 * 4. Map rows into ApplicantCollege[] for the dropdown
 */
export async function fetchCollegeExporterColleges(): Promise<ApplicantCollege[]> {
  if (fetchCollegeExporterCollegesPromise) {
    return fetchCollegeExporterCollegesPromise;
  }

  fetchCollegeExporterCollegesPromise = (async () => {
    try {
      const definition = await fetchExporterDefinition(COLLEGE_EXPORTER_NAME);
      const queryDefinition = resolveExporterQueryType(definition);
      // Empty `{}` when "All" (or chosen query) has no queryAttrDefinition entries.
      const queryParams = buildExporterQueryParams(queryDefinition);

      console.info("[CollegeExporter][mapping] exporter definition columns", {
        exportAttrDefinition: definition.exportAttrDefinition,
        queryType: queryDefinition.queryType,
      });

      const exportResult = await executeExporter({
        exporter: definition.name || COLLEGE_EXPORTER_NAME,
        queryType: queryDefinition.queryType,
        queryParams,
      });

      console.info("[CollegeExporter][debug] export summary", {
        queryType: queryDefinition.queryType,
        requestUrl: exportResult.requestUrl,
        requestBody: exportResult.requestBody,
        status: exportResult.status,
        contentType: exportResult.contentType,
        format: exportResult.format,
        queryAttrCount: queryDefinition.queryAttrDefinition?.length ?? 0,
      });

      if (exportResult.format === "json") {
        const colleges = mapCollegeExporterToApplicantColleges(exportResult.jsonData);
        console.info("[CollegeExporter][debug] mapped colleges from JSON", {
          count: colleges.length,
          sampleNames: colleges.slice(0, 5).map((college) => college.name),
        });
        if (colleges.length === 0) {
          throwIfExporterJsonLooksLikeError(exportResult.jsonData, exportResult.requestUrl);
        }
        return colleges;
      }

      const table = parseExporterCsv(exportResult.csvText);
      const records = exporterCsvToRecords(table);
      console.info("[CollegeExporter][debug] parsed CSV", {
        headers: table.headers,
        rowCount: table.rows.length,
        recordCount: records.length,
      });
      const colleges = mapCollegeExporterToApplicantColleges(records);
      console.info("[CollegeExporter][debug] mapped colleges from CSV", {
        count: colleges.length,
        sampleNames: colleges.slice(0, 5).map((college) => college.name),
      });
      return colleges;
    } finally {
      fetchCollegeExporterCollegesPromise = null;
    }
  })();

  return fetchCollegeExporterCollegesPromise;
}

interface PreferredCollegesPayload {
  collegeIds: number[];
}

export const SHORTLIST_LIMIT_REACHED_MESSAGE =
  "You can shortlist a maximum of 9 colleges. To add another college, please remove one from your current shortlist.";

interface ErrorResponseShape {
  error?: unknown;
  message?: unknown;
  detail?: unknown;
}

function getPreferredCollegeErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseStatus = error.response?.status;
    const responseData = error.response?.data;

    if (responseStatus === 400) {
      return SHORTLIST_LIMIT_REACHED_MESSAGE;
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return "Failed to update preferred college.";
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Failed to update preferred college.";
}

export async function addPreferredColleges(collegeIds: number[]): Promise<void> {
  const payload: PreferredCollegesPayload = { collegeIds };
  try {
    await httpClient.post("/api/v1/applicants/me/preferred-colleges", payload, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
  } catch (error) {
    throw new Error(getPreferredCollegeErrorMessage(error));
  }
}

export async function removePreferredColleges(collegeIds: number[]): Promise<void> {
  const payload: PreferredCollegesPayload = { collegeIds };
  await httpClient.delete("/api/v1/applicants/me/preferred-colleges", {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: payload,
  });
}

/** Contact credentials used when onboarding a college admin or counsellor. */
export interface CollegeOnboardContact {
  name: string;
  email: string;
  mobile: string;
  username: string;
  password: string;
}

/** POST body for `POST /api/v1/spot/super-admin/colleges/{collegeId}/onboard`. */
export interface CollegeOnboardPayload {
  admin: CollegeOnboardContact;
  counsellor?: CollegeOnboardContact | null;
}

function getCollegeOnboardErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }

    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return "Failed to onboard college.";
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Failed to onboard college.";
}

/**
 * Onboards a college with admin (and optional counsellor) credentials.
 * POST /api/v1/spot/super-admin/colleges/{collegeId}/onboard
 */
export async function onboardCollege(collegeId: string | number, payload: CollegeOnboardPayload): Promise<void> {
  try {
    await httpClient.post(
      `/api/v1/spot/super-admin/colleges/${encodeURIComponent(String(collegeId))}/onboard`,
      payload,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(getCollegeOnboardErrorMessage(error));
  }
}

/** Normalized College_Out fields used by the Super Admin onboarded-colleges list. */
export interface SuperAdminCollegeOut {
  collegeId: number;
  name: string;
  shortName?: string;
  /** Institute code from the backend — not used as Short Form in College Admin Settings. */
  collegeCode?: string;
  address?: string;
  /** First address line from College API (`addressLine1`); used by College Admin Settings. */
  addressLine1?: string;
  city?: string;
  stateCode?: string;
  logoUrl?: string;
  adminName?: string;
  adminEmail?: string;
  adminMobile?: string;
  counsellorName?: string;
  counsellorEmail?: string;
  counsellorMobile?: string;
  status?: string;
}

/** One entry from `GET /api/v1/spot/super-admin/colleges/enlist`. */
export interface SuperAdminEnlistedCollege {
  college: SuperAdminCollegeOut;
  onboardedTime: string;
}

function pickContactField(
  contact: Record<string, unknown> | null,
  ...keys: string[]
): string {
  if (!contact) return "";
  for (const key of keys) {
    const value = toStringValue(contact[key]);
    if (value) return value;
  }
  return "";
}

/** First non-empty string for any of `keys` across nested college payload candidates. */
function pickStringFromRecords(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string {
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const value = toStringValue(record[key]);
      if (value) return value;
    }
  }
  return "";
}

/** First nested contact object found under any of `contactKeys` on the candidates. */
function pickNestedContactRecord(
  records: Array<Record<string, unknown> | null | undefined>,
  contactKeys: string[],
): Record<string, unknown> | null {
  for (const record of records) {
    if (!record) continue;
    for (const key of contactKeys) {
      const contact = asRecord(record[key]);
      if (contact) return contact;
    }
  }
  return null;
}

function normalizeCollegeOut(raw: unknown): SuperAdminCollegeOut | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const root = asRecord(raw) ?? {};
  const nestedCollege = asRecord(root.college);
  const nestedDetails = asRecord(root.collegeDetails) ?? asRecord(root.college_details);
  const nestedInfo = asRecord(root.collegeInfo) ?? asRecord(root.college_info);
  const candidates = [root, nestedCollege, nestedDetails, nestedInfo].filter(
    (item): item is Record<string, unknown> => Boolean(item),
  );

  let collegeId: number | null = null;
  let name = "";
  for (const candidate of candidates) {
    if (collegeId === null) {
      collegeId = pickCollegeId(candidate);
    }
    if (!name) {
      name = pickCollegeName(candidate);
    }
    if (collegeId !== null && name) {
      break;
    }
  }

  if (!name) {
    return null;
  }

  const resolvedCollegeId = collegeId ?? 0;

  const shortName = pickStringFromRecords(candidates, [
    "shortName",
    "short_name",
    "shortForm",
    "short_form",
    "shortform",
  ]);

  const collegeCode = pickStringFromRecords(candidates, [
    "collegeCode",
    "college_code",
    "code",
  ]);

  const city = toStringValue(
    pickStringFromRecords(candidates, ["city"])
    || asRecord(root.location)?.city
    || asRecord(nestedCollege?.location)?.city,
  );
  const stateCode = toStringValue(
    pickStringFromRecords(candidates, ["stateCode", "state_code", "state"])
    || asRecord(root.location)?.stateCode
    || asRecord(root.location)?.state_code
    || asRecord(root.location)?.state
    || asRecord(nestedCollege?.location)?.stateCode
    || asRecord(nestedCollege?.location)?.state_code
    || asRecord(nestedCollege?.location)?.state,
  );

  const addressObject =
    asRecord(root.address)
    ?? asRecord(nestedCollege?.address)
    ?? asRecord(nestedDetails?.address)
    ?? asRecord(nestedInfo?.address)
    ?? asRecord(asRecord(root.location)?.address)
    ?? asRecord(asRecord(nestedCollege?.location)?.address);

  // Explicit line-1 field (College Admin Settings input). Do not fall back to combined `address`.
  const addressLine1 = pickStringFromRecords(candidates, [
    "addressLine1",
    "address_line1",
    "address_line_1",
    "address1",
  ]) || toStringValue(
    addressObject?.addressLine1
    ?? addressObject?.address_line1
    ?? addressObject?.address_line_1
    ?? addressObject?.line1
    ?? addressObject?.line_1,
  );

  // Combined / display address used by other surfaces (e.g. Super Admin lists).
  const address = pickStringFromRecords(candidates, [
    "address",
    "fullAddress",
    "full_address",
  ]) || [city, stateCode].filter(Boolean).join(", ");

  const logoUrl = pickStringFromRecords(candidates, [
    "logoUrl",
    "logo_url",
    "logo",
    "imageUrl",
    "image_url",
  ]);

  // Contacts may be flat on the college object, nested under admin/counsellor,
  // or siblings of a wrapped `college` payload — scan every candidate.
  const adminContact = pickNestedContactRecord(candidates, [
    "admin",
    "collegeAdmin",
    "college_admin",
    "adminDetails",
    "admin_details",
    "collegeAdminDetails",
    "college_admin_details",
  ]);
  const counsellorContact = pickNestedContactRecord(candidates, [
    "counsellor",
    "counselor",
    "collegeCounsellor",
    "college_counsellor",
    "collegeCounselor",
    "college_counselor",
    "counsellorDetails",
    "counsellor_details",
    "counselorDetails",
    "counselor_details",
  ]);

  const adminName =
    pickStringFromRecords(candidates, ["adminName", "admin_name"])
    || pickContactField(adminContact, "name", "fullName", "full_name");
  const adminEmail =
    pickStringFromRecords(candidates, ["adminEmail", "admin_email"])
    || pickContactField(adminContact, "email", "username");
  const adminMobile =
    pickStringFromRecords(candidates, [
      "adminMobile",
      "admin_mobile",
      "adminPhone",
      "admin_phone",
    ])
    || pickContactField(adminContact, "mobile", "phone", "phoneNumber", "phone_number");

  const counsellorName =
    pickStringFromRecords(candidates, [
      "counsellorName",
      "counsellor_name",
      "counselorName",
      "counselor_name",
    ])
    || pickContactField(counsellorContact, "name", "fullName", "full_name");
  const counsellorEmail =
    pickStringFromRecords(candidates, [
      "counsellorEmail",
      "counsellor_email",
      "counselorEmail",
      "counselor_email",
    ])
    || pickContactField(counsellorContact, "email", "username");
  const counsellorMobile =
    pickStringFromRecords(candidates, [
      "counsellorMobile",
      "counsellor_mobile",
      "counselorMobile",
      "counselor_mobile",
    ])
    || pickContactField(counsellorContact, "mobile", "phone", "phoneNumber", "phone_number");

  const status = pickStringFromRecords(candidates, [
    "status",
    "onboardingStatus",
    "onboarding_status",
  ]);

  return {
    collegeId: resolvedCollegeId,
    name,
    shortName: shortName || undefined,
    collegeCode: collegeCode || undefined,
    address: address || undefined,
    addressLine1: addressLine1 || undefined,
    city: city || undefined,
    stateCode: stateCode || undefined,
    logoUrl: logoUrl || undefined,
    adminName: adminName || undefined,
    adminEmail: adminEmail || undefined,
    adminMobile: adminMobile || undefined,
    counsellorName: counsellorName || undefined,
    counsellorEmail: counsellorEmail || undefined,
    counsellorMobile: counsellorMobile || undefined,
    status: status || undefined,
  };
}

function normalizeEnlistedCollegeItem(raw: unknown): SuperAdminEnlistedCollege | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const root = asRecord(raw) ?? {};
  const collegeRaw = root.college ?? root.collegeDetails ?? root.college_details ?? root;
  const college = normalizeCollegeOut(collegeRaw);
  if (!college) {
    return null;
  }

  const onboardedTime = toStringValue(
    root.onboarded_time
    ?? root.onboardedTime
    ?? root.onboardedAt
    ?? root.onboarded_at
    ?? root.createdAt
    ?? root.created_at,
  );

  return {
    college,
    onboardedTime,
  };
}

function parseEnlistedCollegesResponse(raw: unknown): SuperAdminEnlistedCollege[] {
  let items: unknown[] = [];

  if (Array.isArray(raw)) {
    items = raw;
  } else {
    const root = asRecord(raw);
    const looksLikeEnlistItem = Boolean(root?.college);
    if (looksLikeEnlistItem) {
      items = [raw];
    } else {
      const container = pickItemsContainer(raw);
      if (container) {
        if (Array.isArray(container.content)) items = container.content;
        else if (Array.isArray(container.items)) items = container.items;
        else if (Array.isArray(container.colleges)) items = container.colleges;
        else if (Array.isArray(container.data)) items = container.data;
      }
    }
  }

  return items
    .map((item) => normalizeEnlistedCollegeItem(item))
    .filter((item): item is SuperAdminEnlistedCollege => item !== null);
}

function getEnlistedCollegesErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return "Failed to load onboarded colleges.";
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Failed to load onboarded colleges.";
}

/**
 * Lists colleges onboarded for SpotRounds Super Admin.
 * GET /api/v1/spot/super-admin/colleges/enlist
 */
export async function fetchEnlistedSuperAdminColleges(): Promise<SuperAdminEnlistedCollege[]> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/super-admin/colleges/enlist", {
      headers: {
        Accept: "application/json",
      },
    });
    console.log("[SuperAdmin][RecentlyUpdatedColleges] API response", response.data);
    return parseEnlistedCollegesResponse(response.data);
  } catch (error) {
    throw new Error(getEnlistedCollegesErrorMessage(error));
  }
}

function preferCollegeAddress(
  primary?: string,
  secondary?: string,
): string | undefined {
  const a = primary?.trim() || "";
  const b = secondary?.trim() || "";
  if (a && b) {
    // Prefer the longer value. College Admin payloads often put the full Settings
    // address on the wrapper while the nested college only has city/state, which
    // normalizeCollegeOut synthesizes into a shorter "city, state" address.
    return a.length >= b.length ? a : b;
  }
  return a || b || undefined;
}

/**
 * College Admin Settings "College Address" must use `addressLine1` only —
 * never the combined `address` / city+state fallback.
 */
function resolveCollegeAdminSettingsAddress(
  wrapper?: SuperAdminCollegeOut | null,
  nested?: SuperAdminCollegeOut | null,
): string | undefined {
  const fromLine1 = preferCollegeAddress(wrapper?.addressLine1, nested?.addressLine1);
  if (fromLine1) return fromLine1;

  // Also accept addressLine1 when the raw mapper only exposed it via a nested shape
  // that landed on addressLine1 of either side after normalize.
  return undefined;
}

function logCollegeAdminShortFormMapping(college: SuperAdminCollegeOut): SuperAdminCollegeOut {
  console.log("[college-settings] College API short form mapping", {
    backendShortForm: college.shortName ?? null,
    backendCollegeCode: college.collegeCode ?? null,
    finalInputValue: college.shortName?.trim() ? college.shortName.trim().toUpperCase() : "",
  });
  return college;
}

function parseCollegeAdminCollegeResponse(raw: unknown): SuperAdminCollegeOut {
  const root = asRecord(raw);

  console.log("[college-address] Raw API response", raw);

  // Prefer an explicit nested college object when present, then fill any missing
  // contact fields from the wrapper (admin/counsellor often sit beside `college`).
  if (root) {
    const nestedRaw =
      root.college
      ?? root.collegeDetails
      ?? root.college_details
      ?? root.data
      ?? root.result;
    const nested = nestedRaw !== undefined ? normalizeCollegeOut(nestedRaw) : null;
    if (nested) {
      const wrapper = normalizeCollegeOut(raw);
      const addressLine1 = resolveCollegeAdminSettingsAddress(wrapper, nested);
      // Settings input binds to `address` on the UI college model; populate it from addressLine1 only.
      const settingsAddress = addressLine1;
      console.log("[college-address] Address after mapping", {
        nestedAddressLine1: nested.addressLine1 ?? null,
        wrapperAddressLine1: wrapper?.addressLine1 ?? null,
        combinedAddressIgnoredForSettings: preferCollegeAddress(wrapper?.address, nested.address) ?? null,
        settingsAddressFromAddressLine1: settingsAddress ?? null,
      });
      return logCollegeAdminShortFormMapping({
        ...nested,
        shortName: nested.shortName ?? wrapper?.shortName,
        collegeCode: nested.collegeCode ?? wrapper?.collegeCode,
        addressLine1,
        address: settingsAddress,
        city: nested.city ?? wrapper?.city,
        stateCode: nested.stateCode ?? wrapper?.stateCode,
        logoUrl: nested.logoUrl ?? wrapper?.logoUrl,
        adminName: nested.adminName ?? wrapper?.adminName,
        adminEmail: nested.adminEmail ?? wrapper?.adminEmail,
        adminMobile: nested.adminMobile ?? wrapper?.adminMobile,
        counsellorName: nested.counsellorName ?? wrapper?.counsellorName,
        counsellorEmail: nested.counsellorEmail ?? wrapper?.counsellorEmail,
        counsellorMobile: nested.counsellorMobile ?? wrapper?.counsellorMobile,
        status: nested.status ?? wrapper?.status,
      });
    }
  }

  const direct = normalizeCollegeOut(raw);
  if (direct) {
    const settingsAddress = direct.addressLine1?.trim() || undefined;
    console.log("[college-address] Address after mapping (direct)", {
      addressLine1: direct.addressLine1 ?? null,
      combinedAddressIgnoredForSettings: direct.address ?? null,
      settingsAddressFromAddressLine1: settingsAddress ?? null,
    });
    return logCollegeAdminShortFormMapping({
      ...direct,
      address: settingsAddress,
      addressLine1: settingsAddress,
    });
  }

  throw new Error("Invalid college response.");
}

function getCollegeAdminCollegeErrorMessage(
  error: unknown,
  fallback = "Failed to load college.",
): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }
    if (status === 404) {
      return "College not found for this admin.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return fallback;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

/**
 * Returns the college assigned to the signed-in SpotRounds College Admin.
 * GET /api/v1/spot/college-admin/college
 */
export async function fetchCollegeAdminCollege(): Promise<SuperAdminCollegeOut> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/college", {
      headers: {
        Accept: "application/json",
      },
    });
    return parseCollegeAdminCollegeResponse(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminCollegeErrorMessage(error));
  }
}

/** Payload for College Admin Settings college identity update. */
export interface UpdateCollegeAdminCollegePayload {
  address: string;
  /** Backend field name is `shortform` (maps to UI `shortName`). */
  shortform: string;
}

/**
 * Updates college address / short form for the signed-in College Admin.
 * PUT /api/v1/spot/college-admin/college
 * Body: { address, shortform }
 * Auth: Bearer (College Admin) or X-Spot-Impersonation (Super Admin impersonation) via httpClient.
 */
export async function updateCollegeAdminCollege(
  payload: UpdateCollegeAdminCollegePayload,
): Promise<SuperAdminCollegeOut | void> {
  const body = {
    address: payload.address.trim(),
    shortform: payload.shortform.trim(),
  };

  console.log("[college-settings] PUT /api/v1/spot/college-admin/college request body", body);

  try {
    const response = await httpClient.put<unknown>("/api/v1/spot/college-admin/college", body, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    console.log("[college-settings] PUT /api/v1/spot/college-admin/college response", {
      status: response.status,
      data: response.data,
    });

    if (response.data == null || response.data === "") {
      return;
    }
    return parseCollegeAdminCollegeResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[college-settings] PUT /api/v1/spot/college-admin/college failed", {
        status: error.response?.status,
        requestBody: body,
        responseData: error.response?.data,
        message: error.message,
      });
    } else {
      console.error("[college-settings] PUT /api/v1/spot/college-admin/college failed (non-HTTP)", error);
    }
    throw new Error(
      getCollegeAdminCollegeErrorMessage(error, "Failed to update college settings."),
    );
  }
}

/**
 * Counsellor contact from:
 * - `GET /api/v1/spot/college-admin/counsellor` (signed-in College Admin)
 * - `GET /api/v1/spot/super-admin/colleges/{collegeId}/counsellor` (Super Admin)
 */
export interface CollegeAdminCounsellorOut {
  name: string;
  email: string;
  mobile: string;
}

function parseCollegeAdminCounsellorResponse(raw: unknown): CollegeAdminCounsellorOut {
  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid counsellor response.");
  }

  const nested =
    pickNestedContactRecord([root], [
      "counsellor",
      "counselor",
      "collegeCounsellor",
      "college_counsellor",
      "collegeCounselor",
      "college_counselor",
      "counsellorDetails",
      "counsellor_details",
      "counselorDetails",
      "counselor_details",
      "data",
      "result",
    ]) ?? root;

  const candidates = [nested, root];

  const name =
    pickStringFromRecords(candidates, [
      "name",
      "fullName",
      "full_name",
      "counsellorName",
      "counsellor_name",
      "counselorName",
      "counselor_name",
    ]) || pickContactField(nested, "name", "fullName", "full_name");

  const email =
    pickStringFromRecords(candidates, [
      "email",
      "username",
      "counsellorEmail",
      "counsellor_email",
      "counselorEmail",
      "counselor_email",
    ]) || pickContactField(nested, "email", "username");

  const mobile =
    pickStringFromRecords(candidates, [
      "mobile",
      "phone",
      "phoneNumber",
      "phone_number",
      "counsellorMobile",
      "counsellor_mobile",
      "counselorMobile",
      "counselor_mobile",
    ]) || pickContactField(nested, "mobile", "phone", "phoneNumber", "phone_number");

  // Accept an empty contact (no counsellor assigned) as long as the payload is shaped.
  return { name, email, mobile };
}

function getCollegeAdminCounsellorErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }
    if (status === 404) {
      return "Counsellor not found for this college.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return "Failed to load counsellor.";
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Failed to load counsellor.";
}

export type CollegeAdminRequestOptions = {
  /** Database college.id — required for Super Admin college-scoped admin/counsellor APIs. */
  collegeId?: number | string;
};

/**
 * Returns counsellor contact details.
 * - College Admin: GET /api/v1/spot/college-admin/counsellor
 * - Super Admin: GET /api/v1/spot/super-admin/colleges/{collegeId}/counsellor
 */
export async function fetchCollegeAdminCounsellor(
  options?: CollegeAdminRequestOptions,
): Promise<CollegeAdminCounsellorOut> {
  try {
    const collegeId = options?.collegeId;
    const url =
      collegeId != null && String(collegeId).trim() !== ""
        ? `/api/v1/spot/super-admin/colleges/${encodeURIComponent(String(collegeId))}/counsellor`
        : "/api/v1/spot/college-admin/counsellor";

    console.info("[SuperAdmin][service] fetchCollegeAdminCounsellor called", {
      collegeId,
      url,
      note: "path param must be database college.id, never college.code",
    });

    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
      },
    });
    console.info("[SuperAdmin][service] fetchCollegeAdminCounsellor response", response.data);
    return parseCollegeAdminCounsellorResponse(response.data);
  } catch (error) {
    console.info("[SuperAdmin][service] fetchCollegeAdminCounsellor error", error);
    throw new Error(getCollegeAdminCounsellorErrorMessage(error));
  }
}

/**
 * College Admin profile from:
 * - `GET /api/v1/spot/college-admin/me` (signed-in College Admin)
 * - `GET /api/v1/spot/super-admin/colleges/{collegeId}/admin` (Super Admin)
 */
export interface CollegeAdminMeOut {
  id?: number;
  name: string;
  email: string;
  mobile: string;
  collegeId?: number;
  username?: string;
}

function parseCollegeAdminMeResponse(raw: unknown): CollegeAdminMeOut {
  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid college admin response.");
  }

  const nested =
    pickNestedContactRecord([root], [
      "admin",
      "collegeAdmin",
      "college_admin",
      "adminDetails",
      "admin_details",
      "me",
      "user",
      "data",
      "result",
    ]) ?? root;

  const candidates = [nested, root];

  const name =
    pickStringFromRecords(candidates, [
      "name",
      "fullName",
      "full_name",
      "adminName",
      "admin_name",
    ]) || pickContactField(nested, "name", "fullName", "full_name");

  const email =
    pickStringFromRecords(candidates, [
      "email",
      "adminEmail",
      "admin_email",
    ]) || pickContactField(nested, "email");

  const mobile =
    pickStringFromRecords(candidates, [
      "mobile",
      "phone",
      "phoneNumber",
      "phone_number",
      "adminMobile",
      "admin_mobile",
    ]) || pickContactField(nested, "mobile", "phone", "phoneNumber", "phone_number");

  const username =
    pickStringFromRecords(candidates, ["username", "userName", "user_name"]) || undefined;

  const id =
    toNumber(nested.id)
    ?? toNumber(root.id)
    ?? undefined;

  const collegeId =
    toNumber(nested.collegeId)
    ?? toNumber(nested.college_id)
    ?? toNumber(root.collegeId)
    ?? toNumber(root.college_id)
    ?? pickCollegeId(nested)
    ?? pickCollegeId(root)
    ?? undefined;

  return {
    id: id ?? undefined,
    name,
    email,
    mobile,
    collegeId: collegeId ?? undefined,
    username: username || undefined,
  };
}

function getCollegeAdminMeErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }
    if (status === 404) {
      return "College admin profile not found.";
    }

    const responseData = error.response.data;
    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
    }

    return "Failed to load college admin.";
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return "Failed to load college admin.";
}

/**
 * Returns college admin profile details.
 * - College Admin: GET /api/v1/spot/college-admin/me
 * - Super Admin: GET /api/v1/spot/super-admin/colleges/{collegeId}/admin
 */
export async function fetchCollegeAdminMe(
  options?: CollegeAdminRequestOptions,
): Promise<CollegeAdminMeOut> {
  try {
    const collegeId = options?.collegeId;
    const url =
      collegeId != null && String(collegeId).trim() !== ""
        ? `/api/v1/spot/super-admin/colleges/${encodeURIComponent(String(collegeId))}/admin`
        : "/api/v1/spot/college-admin/me";

    console.info("[SuperAdmin][service] fetchCollegeAdminMe called", {
      collegeId,
      url,
      note: "path param must be database college.id, never college.code",
    });

    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
      },
    });
    console.info("[SuperAdmin][service] fetchCollegeAdminMe response", response.data);
    return parseCollegeAdminMeResponse(response.data);
  } catch (error) {
    console.info("[SuperAdmin][service] fetchCollegeAdminMe error", error);
    throw new Error(getCollegeAdminMeErrorMessage(error));
  }
}

/** PUT body for `PUT /api/v1/spot/college-admin/rounds/{roundId}` — all fields optional. */
export interface UpdateCollegeAdminRoundPayload {
  startDate?: string;
  endDate?: string;
  status?: string;
}

/** Normalized round from College Admin rounds APIs. */
export interface CollegeAdminRoundOut {
  id: number;
  previousRoundId: number | null;
  startDate: string;
  endDate: string;
  status: string;
  name?: string;
}

function getCollegeAdminRoundsErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return "Unable to connect to server.";
    }

    const status = error.response.status;
    const responseData = error.response.data;

    if (status === 400) {
      console.log("[CollegeAdminRounds] 400 error response", responseData);
    }

    if (status === 401) {
      return "Authentication failed.";
    }
    if (status === 403) {
      return "Access denied.";
    }

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData;
    }
    if (responseData && typeof responseData === "object") {
      const typedData = responseData as ErrorResponseShape & Record<string, unknown>;
      if (typeof typedData.message === "string" && typedData.message.trim()) {
        return typedData.message;
      }
      if (typeof typedData.detail === "string" && typedData.detail.trim()) {
        return typedData.detail;
      }
      if (typeof typedData.error === "string" && typedData.error.trim()) {
        return typedData.error;
      }
      const errors = typedData.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        const first = errors[0];
        if (typeof first === "string" && first.trim()) {
          return first;
        }
        if (first && typeof first === "object") {
          const nested = first as Record<string, unknown>;
          const nestedMessage = nested.message ?? nested.defaultMessage ?? nested.error;
          if (typeof nestedMessage === "string" && nestedMessage.trim()) {
            return nestedMessage;
          }
        }
      }
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

function normalizeCollegeAdminRound(raw: unknown): CollegeAdminRoundOut | null {
  const root = asRecord(raw);
  if (!root) return null;

  const nested =
    asRecord(root.round)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  const id =
    toNumber(nested.id)
    ?? toNumber(nested.roundId)
    ?? toNumber(nested.round_id)
    ?? toNumber(root.id)
    ?? toNumber(root.roundId)
    ?? toNumber(root.round_id);

  if (id === null) return null;

  const previousRaw =
    nested.previousRoundId
    ?? nested.previous_round_id
    ?? root.previousRoundId
    ?? root.previous_round_id;
  const previousRoundId =
    previousRaw === null || previousRaw === undefined || previousRaw === ""
      ? null
      : toNumber(previousRaw);

  const startDate = pickRoundDateFromRecords([nested, root], [
    "startDate",
    "start_date",
    "dayStart",
    "day_start",
  ]);
  const endDate = pickRoundDateFromRecords([nested, root], [
    "endDate",
    "end_date",
    "dayEnd",
    "day_end",
  ]);
  const status =
    pickStringFromRecords([nested, root], ["status", "roundStatus", "round_status"]) || "Not Started";
  const name = pickStringFromRecords([nested, root], ["name", "title", "roundName", "round_name"]);

  return {
    id,
    previousRoundId: previousRoundId === null ? null : previousRoundId,
    startDate,
    endDate,
    status,
    name: name || undefined,
  };
}

function parseCollegeAdminRoundsResponse(raw: unknown): CollegeAdminRoundOut[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeCollegeAdminRound(item))
      .filter((item): item is CollegeAdminRoundOut => Boolean(item));
  }

  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid rounds response.");
  }

  const candidates = [
    root.rounds,
    root.content,
    root.items,
    root.data,
    root.results,
    asRecord(root.data)?.rounds,
    asRecord(root.data)?.content,
    asRecord(root.result)?.rounds,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => normalizeCollegeAdminRound(item))
        .filter((item): item is CollegeAdminRoundOut => Boolean(item));
    }
  }

  const single = normalizeCollegeAdminRound(raw);
  if (single) return [single];

  throw new Error("Invalid rounds response.");
}

/**
 * Lists SpotRounds for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/rounds
 */
export async function fetchCollegeAdminRounds(): Promise<CollegeAdminRoundOut[]> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/rounds", {
      headers: {
        Accept: "application/json",
      },
    });
    return sortSpotRoundsBySequenceAsc(
      parseCollegeAdminRoundsResponse(response.data),
      "[SpotRound][AdminRounds]",
    );
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load rounds."));
  }
}

/**
 * Returns a single SpotRound for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/rounds/{roundId}
 */
export async function fetchCollegeAdminRoundById(
  roundId: number,
): Promise<CollegeAdminRoundOut> {
  try {
    const response = await httpClient.get<unknown>(
      `/api/v1/spot/college-admin/rounds/${encodeURIComponent(String(roundId))}`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
    const parsed =
      normalizeCollegeAdminRound(response.data)
      ?? parseCollegeAdminRoundsResponse(response.data)[0];
    if (!parsed) {
      throw new Error("Invalid round response.");
    }
    return parsed;
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load round."));
  }
}

/** Normalized spot seat row from College Admin round spots API. */
export interface CollegeAdminSpotOut {
  id: number;
  spotRoundId: number | null;
  exam: string;
  courseName: string;
  /** Short label for seat-matrix column headers; fall back to courseName when empty. */
  courseShortForm: string | null;
  courseCode: string;
  courseId: number | null;
  category: string;
  vacantSeatsCount: number | null;
  updatedBy?: string;
  updatedOn?: string;
}

/** Coerce a scalar or `{ id | courseId | spotDetailId }` object into a finite number. */
function toIdNumber(value: unknown): number | null {
  const direct = toNumber(value);
  if (direct !== null) return direct;
  const record = asRecord(value);
  if (!record) return null;
  return (
    toNumber(record.spotDetailId)
    ?? toNumber(record.spot_detail_id)
    ?? toNumber(record.courseId)
    ?? toNumber(record.course_id)
    ?? toNumber(record.id)
  );
}

/** First finite id for any of `keys` across records (exact key, then normalized key). */
function pickNumberFromRecords(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): number | null {
  const normalizedWanted = new Set(keys.map((key) => normalizeLookupKey(key)));
  for (const record of records) {
    if (!record) continue;
    for (const key of keys) {
      const value = toIdNumber(record[key]);
      if (value !== null) return value;
    }
    for (const [key, value] of Object.entries(record)) {
      if (!normalizedWanted.has(normalizeLookupKey(key))) continue;
      const parsed = toIdNumber(value);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

function normalizeCollegeAdminSpot(raw: unknown): CollegeAdminSpotOut | null {
  const root = asRecord(raw);
  if (!root) return null;

  // Prefer nested spot-detail payloads when the list item is a wrapper DTO.
  const nested =
    asRecord(root.spotDetail)
    ?? asRecord(root.spot_detail)
    ?? asRecord(root.spot)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  // Prefer explicit spotDetailId over generic `id` (wrappers sometimes reuse `id` for course).
  const spotDetailId = pickNumberFromRecords([nested, root], [
    "spotDetailId",
    "spot_detail_id",
  ]);
  const genericId = pickNumberFromRecords([nested, root], [
    "spotId",
    "spot_id",
    "id",
  ]);
  const id = spotDetailId ?? genericId;

  const spotRoundId = pickNumberFromRecords([nested, root], [
    "spotRoundId",
    "spot_round_id",
    "roundId",
    "round_id",
  ]);

  const exam = pickStringFromRecords([nested, root], [
    "exam",
    "examName",
    "exam_name",
    "examType",
    "exam_type",
  ]);
  // Prefer explicit course fields; avoid loose "name" fallback so category/exam are not mistaken for courses.
  const courseRecord =
    asRecord(nested.course)
    ?? asRecord(root.course)
    ?? asRecord(nested.courseInfo)
    ?? asRecord(root.courseInfo)
    ?? asRecord(nested.courseId)
    ?? asRecord(root.courseId);
  const courseName =
    pickStringFromRecords([nested, root, courseRecord], [
      "courseName",
      "course_name",
      "stream",
      "streamName",
      "stream_name",
    ])
    // Only read loose `name` from the nested course object (not the spot row).
    || (courseRecord ? toStringValue(courseRecord.name) : "");
  const courseShortFormRaw = pickStringFromRecords([nested, root, courseRecord], [
    "courseShortForm",
    "course_short_form",
    "courseShortform",
    "shortForm",
    "short_form",
  ]);
  const courseShortForm = courseShortFormRaw || null;
  const courseCode = pickStringFromRecords([nested, root, courseRecord], [
    "courseCode",
    "course_code",
    "code",
  ]);
  const category = pickStringFromRecords([nested, root], [
    "category",
    "categoryName",
    "category_name",
    "seatCategory",
    "seat_category",
  ]);

  // Sample payloads may omit id; accept rows that still have seat-matrix fields.
  if (id === null && !courseName && !category) {
    return null;
  }

  // courseId may be flat, nested under `course` / `courseInfo`, object-shaped, or loose `course`.
  // When spotDetailId and id both exist and differ, some payloads store courseId in `id`.
  const courseId =
    pickNumberFromRecords([nested, root, courseRecord], [
      "courseId",
      "course_id",
      "CourseId",
    ])
    ?? (
      spotDetailId != null
      && genericId != null
      && genericId !== spotDetailId
        ? genericId
        : null
    )
    ?? toIdNumber(nested.course)
    ?? toIdNumber(root.course);
  const vacantRaw =
    nested.vacantSeatsCount
    ?? nested.vacant_seats_count
    ?? nested.vacantSeats
    ?? nested.vacant_seats
    ?? nested.seats
    ?? root.vacantSeatsCount
    ?? root.vacant_seats_count;
  const vacantSeatsCount = toNumber(vacantRaw);
  const updatedBy = pickStringFromRecords([nested, root], ["updatedBy", "updated_by"]);
  const updatedOn = toRoundDateValue(
    nested.updatedOn ?? nested.updated_on ?? root.updatedOn ?? root.updated_on,
  );

  return {
    id: id ?? 0,
    spotRoundId,
    exam,
    courseName,
    courseShortForm,
    courseCode,
    courseId,
    category,
    vacantSeatsCount,
    updatedBy: updatedBy || undefined,
    updatedOn: updatedOn || undefined,
  };
}

function parseCollegeAdminSpotsArray(items: unknown[]): CollegeAdminSpotOut[] {
  const parsed = items.map((item) => normalizeCollegeAdminSpot(item));
  return parsed.filter((item): item is CollegeAdminSpotOut => Boolean(item));
}

function parseCollegeAdminSpotsResponse(raw: unknown): CollegeAdminSpotOut[] {
  if (Array.isArray(raw)) {
    return parseCollegeAdminSpotsArray(raw);
  }

  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid spots response.");
  }

  const candidates = [
    root.spots,
    root.spotDetails,
    root.spot_details,
    root.content,
    root.items,
    root.data,
    root.results,
    asRecord(root.data)?.spots,
    asRecord(root.data)?.spotDetails,
    asRecord(root.data)?.spot_details,
    asRecord(root.data)?.content,
    asRecord(root.result)?.spots,
    asRecord(root.result)?.spotDetails,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return parseCollegeAdminSpotsArray(candidate);
    }
  }

  const single = normalizeCollegeAdminSpot(raw);
  if (single) return [single];

  throw new Error("Invalid spots response.");
}

/**
 * Lists spot seat rows for a College Admin SpotRound.
 * GET /api/v1/spot/college-admin/rounds/{roundId}/spots
 */
export async function fetchCollegeAdminRoundSpots(
  roundId: number,
): Promise<CollegeAdminSpotOut[]> {
  try {
    const response = await httpClient.get<unknown>(
      `/api/v1/spot/college-admin/rounds/${encodeURIComponent(String(roundId))}/spots`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
    // TEMP DEBUG: verify raw API payload includes courseShortForm
    const raw = response.data;
    const rawRows = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { spots?: unknown })?.spots)
        ? (raw as { spots: unknown[] }).spots
        : Array.isArray((raw as { data?: unknown })?.data)
          ? (raw as { data: unknown[] }).data
          : [];
    const sampleRaw = rawRows[0];
    console.log("[SpotRounds][API] sample raw spots row", sampleRaw);
    if (sampleRaw && typeof sampleRaw === "object") {
      const row = sampleRaw as Record<string, unknown>;
      const nestedCourse = row.course && typeof row.course === "object"
        ? (row.course as Record<string, unknown>)
        : null;
      console.log(
        "[SpotRounds][API] courseName=",
        row.courseName,
        "courseShortForm=",
        row.courseShortForm,
        "courseId=",
        row.courseId ?? row.course_id ?? nestedCourse?.id ?? nestedCourse?.courseId,
        "keys=",
        Object.keys(row),
      );
    }

    const normalized = parseCollegeAdminSpotsResponse(response.data);
    console.log("[SpotRounds][service] sample normalized spot", normalized[0]);
    console.log(
      "[SpotEdit] normalized spots courseId presence",
      normalized.slice(0, 5).map((spot) => ({
        spotDetailId: spot.id > 0 ? spot.id : null,
        courseId: spot.courseId,
        courseName: spot.courseName,
        courseShortForm: spot.courseShortForm,
        category: spot.category,
        exam: spot.exam,
      })),
    );
    return normalized;
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load spots."));
  }
}

/** One row in `PUT /api/v1/spot/college-admin/rounds/{roundId}/spots`. */
export interface UpdateCollegeAdminSpotItem {
  courseId: number;
  exam: string;
  category: string;
  vacantSeatsCount: number;
}

/** PUT body for updating spot seat counts for a round. */
export interface UpdateCollegeAdminSpotsPayload {
  spots: UpdateCollegeAdminSpotItem[];
}

/**
 * Upserts spot seat rows for a College Admin SpotRound.
 * PUT /api/v1/spot/college-admin/rounds/{roundId}/spots
 */
export async function updateCollegeAdminRoundSpots(
  roundId: number,
  payload: UpdateCollegeAdminSpotsPayload,
): Promise<CollegeAdminSpotOut[]> {
  try {
    const response = await httpClient.put<unknown>(
      `/api/v1/spot/college-admin/rounds/${encodeURIComponent(String(roundId))}/spots`,
      payload,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    return parseCollegeAdminSpotsResponse(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update spots."));
  }
}

/**
 * Adds a SpotRound for the signed-in College Admin.
 * POST /api/v1/spot/college-admin/rounds/add — no request body; expects 201.
 */
export async function addCollegeAdminRound(): Promise<void> {
  try {
    await httpClient.post<unknown>("/api/v1/spot/college-admin/rounds/add", undefined, {
      headers: {
        Accept: "application/json",
      },
    });
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to add round."));
  }
}

/**
 * Deletes a SpotRound for the signed-in College Admin.
 * DELETE /api/v1/spot/college-admin/rounds/{roundId} — no request body; expects 204.
 */
export async function deleteCollegeAdminRound(roundId: number): Promise<void> {
  try {
    await httpClient.delete<unknown>(
      `/api/v1/spot/college-admin/rounds/${encodeURIComponent(String(roundId))}`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to delete round."));
  }
}

/**
 * Updates a SpotRound for the signed-in College Admin.
 * PUT /api/v1/spot/college-admin/rounds/{roundId}
 */
export async function updateCollegeAdminRound(
  roundId: number,
  payload: UpdateCollegeAdminRoundPayload,
): Promise<CollegeAdminRoundOut | void> {
  const body: Record<string, string> = {};
  if (payload.startDate !== undefined) body.startDate = payload.startDate;
  if (payload.endDate !== undefined) body.endDate = payload.endDate;
  if (payload.status !== undefined) body.status = payload.status;

  try {
    const response = await httpClient.put<unknown>(
      `/api/v1/spot/college-admin/rounds/${encodeURIComponent(String(roundId))}`,
      body,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    if (response.data == null || response.data === "") {
      return;
    }
    const updated = normalizeCollegeAdminRound(response.data);
    if (updated) return updated;
    const parsed = parseCollegeAdminRoundsResponse(response.data);
    return parsed[0];
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update round."));
  }
}

// ---------------------------------------------------------------------------
// Circulars / Bank Details / Document Checklist
// ---------------------------------------------------------------------------

/** Normalized circular from College Admin / Public circulars APIs. */
export interface CollegeAdminCircularOut {
  /** Backend asset id used for PUT/DELETE path params. */
  id: number | string;
  title: string;
  date: string;
  filename: string;
  /** Uploaded file id from POST /api/v1/files — used to download the PDF. */
  fileId?: number;
}

/**
 * Create circular input. The PDF is uploaded via `/api/v1/files` first;
 * circulars receive JSON `{ title, date, fileId }` only.
 */
export interface CreateCollegeAdminCircularPayload {
  title: string;
  date: string;
  /** Kept for callers; not sent on the circulars JSON endpoint. */
  filename?: string;
  /** PDF document required by the Circulars API. */
  file: File;
}

/**
 * Update circular input. When `file` is set it is uploaded via `/api/v1/files`
 * and the returned id is sent as `fileId`; otherwise the existing file is kept.
 */
export interface UpdateCollegeAdminCircularPayload {
  title: string;
  date: string;
  /** Kept for callers; not sent on the circulars JSON endpoint. */
  filename?: string;
  /** Optional replacement PDF; omit to keep the existing document. */
  file?: File;
}

/** Normalized bank details from College Admin / Public bank-details APIs. */
export interface CollegeAdminBankDetailsOut {
  accountName: string;
  accountNumber: string;
  ifsc: string;
  bankName: string;
  branch: string;
  upiId: string;
}

/** PUT body for `PUT /api/v1/spot/college-admin/bank-details`. */
export type UpdateCollegeAdminBankDetailsPayload = CollegeAdminBankDetailsOut;

/** Wire payload for bank-details PUT (includes backend aliases). */
function toCollegeAdminBankDetailsRequestBody(
  payload: UpdateCollegeAdminBankDetailsPayload,
): Record<string, string> {
  const accountName = payload.accountName?.trim() || "";
  const accountNumber = payload.accountNumber?.trim() || "";
  const ifsc = payload.ifsc?.trim() || "";
  const bankName = payload.bankName?.trim() || "";
  const branch = payload.branch?.trim() || "";
  const upiId = payload.upiId?.trim() || "";

  return {
    accountName,
    accountNumber,
    ifsc,
    bankName,
    // UI / normalized field
    branch,
    // Backend DTO commonly uses branchName — send both so the value is not dropped.
    branchName: branch,
    upiId,
  };
}

/** One document checklist row. */
export interface CollegeAdminDocumentItemOut {
  name: string;
  required: boolean;
}

/** PUT body for `PUT /api/v1/spot/college-admin/document-checklist`. */
export interface UpdateCollegeAdminDocumentChecklistPayload {
  items: CollegeAdminDocumentItemOut[];
}

/** Wire payload matching the backend contract: `{ items: [{ name, required }] }`. */
function toCollegeAdminDocumentChecklistRequestBody(
  payload: UpdateCollegeAdminDocumentChecklistPayload,
): { items: CollegeAdminDocumentItemOut[] } {
  return {
    items: payload.items.map((item) => ({
      name: item.name?.trim() || "",
      required: Boolean(item.required),
    })),
  };
}

function normalizeCollegeAdminCircular(raw: unknown): CollegeAdminCircularOut | null {
  const root = asRecord(raw);
  if (!root) return null;

  const nested =
    asRecord(root.circular)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  const id =
    toNumber(nested.assetId)
    ?? toNumber(nested.asset_id)
    ?? toNumber(root.assetId)
    ?? toNumber(root.asset_id)
    ?? toNumber(nested.id)
    ?? toNumber(nested.circularId)
    ?? toNumber(nested.circular_id)
    ?? toNumber(root.id)
    ?? toStringValue(nested.assetId)
    ?? toStringValue(nested.asset_id)
    ?? toStringValue(root.assetId)
    ?? toStringValue(root.asset_id)
    ?? toStringValue(nested.id)
    ?? toStringValue(root.id);

  const title = pickStringFromRecords([nested, root], [
    "title",
    "name",
    "circularTitle",
    "circular_title",
    "subject",
  ]);
  if (!title && (id === null || id === "")) return null;

  const date =
    toRoundDateValue(
      nested.date
      ?? nested.circularDate
      ?? nested.circular_date
      ?? nested.publishedDate
      ?? nested.published_date
      ?? nested.postedDate
      ?? nested.posted_date
      ?? root.date
      ?? root.circularDate
      ?? root.circular_date,
    ) || pickStringFromRecords([nested, root], ["date"]);

  const fileRecord =
    asRecord(nested.file)
    ?? asRecord(nested.document)
    ?? asRecord(root.file)
    ?? asRecord(root.document);

  const filename = pickStringFromRecords([nested, root, fileRecord], [
    "filename",
    "fileName",
    "file_name",
    "documentName",
    "document_name",
    "originalFilename",
    "original_filename",
    "name",
  ]);
  // Only treat `file` as a filename when it is a plain string (not a nested object).
  const fileAsFilename =
    typeof nested.file === "string"
      ? nested.file.trim()
      : typeof root.file === "string"
        ? root.file.trim()
        : "";

  const fileId =
    toNumber(nested.fileId)
    ?? toNumber(nested.file_id)
    ?? toNumber(nested.documentId)
    ?? toNumber(nested.document_id)
    ?? toNumber(root.fileId)
    ?? toNumber(root.file_id)
    ?? toNumber(root.documentId)
    ?? toNumber(root.document_id)
    ?? toNumber(fileRecord?.id)
    ?? toNumber(fileRecord?.fileId)
    ?? toNumber(fileRecord?.file_id)
    ?? null;

  if (fileId == null) {
    console.warn("[CircularDownload] Circular row has no resolvable fileId", {
      keys: Object.keys(nested),
      assetId: nested.assetId ?? nested.asset_id ?? root.assetId,
      fileId: nested.fileId ?? nested.file_id ?? root.fileId,
      documentId: nested.documentId ?? nested.document_id,
      file: nested.file ?? root.file,
      downloadUrl: nested.downloadUrl ?? nested.download_url ?? nested.url,
      raw: nested,
    });
  }

  return {
    id: id === null || id === "" ? title : id,
    title: title || "Circular",
    date,
    filename:
      filename
      || fileAsFilename
      || (title ? `${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf` : ""),
    ...(fileId != null ? { fileId } : {}),
  };
}

function parseCollegeAdminCircularsResponse(raw: unknown): CollegeAdminCircularOut[] {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeCollegeAdminCircular(item))
      .filter((item): item is CollegeAdminCircularOut => Boolean(item));
  }

  const root = asRecord(raw);
  if (!root) {
    throw new Error("Invalid circulars response.");
  }

  const candidates = [
    root.circulars,
    root.content,
    root.items,
    root.data,
    root.results,
    asRecord(root.data)?.circulars,
    asRecord(root.data)?.content,
    asRecord(root.result)?.circulars,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate
        .map((item) => normalizeCollegeAdminCircular(item))
        .filter((item): item is CollegeAdminCircularOut => Boolean(item));
    }
  }

  const single = normalizeCollegeAdminCircular(raw);
  if (single) return [single];

  // Empty object / null payload → no circulars yet.
  if (!root.circulars && !root.content && !root.items && !root.data && !root.results) {
    return [];
  }

  throw new Error("Invalid circulars response.");
}

function normalizeCollegeAdminBankDetails(raw: unknown): CollegeAdminBankDetailsOut {
  const root = asRecord(raw) ?? {};
  const nested =
    asRecord(root.bankDetails)
    ?? asRecord(root.bank_details)
    ?? asRecord(root.bank)
    ?? asRecord(root.data)
    ?? asRecord(root.result)
    ?? root;

  return {
    accountName: pickStringFromRecords([nested, root], [
      "accountName",
      "account_name",
      "accountHolderName",
      "account_holder_name",
      "holderName",
      "holder_name",
    ]),
    accountNumber: pickStringFromRecords([nested, root], [
      "accountNumber",
      "account_number",
      "accountNo",
      "account_no",
    ]),
    ifsc: pickStringFromRecords([nested, root], [
      "ifsc",
      "ifscCode",
      "ifsc_code",
      "IFSC",
    ]),
    bankName: pickStringFromRecords([nested, root], [
      "bankName",
      "bank_name",
      "name",
    ]),
    branch: pickStringFromRecords([nested, root], [
      "branch",
      "branchName",
      "branch_name",
    ]),
    upiId: pickStringFromRecords([nested, root], [
      "upiId",
      "upi_id",
      "upi",
      "upiAddress",
      "upi_address",
    ]),
  };
}

/** Non-array object record (arrays are objects in JS and must not be treated as maps). */
function asObjectRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Read a field by normalized key (camelCase / PascalCase / snake_case). */
function pickFieldIgnoreCase(
  record: Record<string, unknown> | null | undefined,
  keys: string[],
): unknown {
  if (!record) return undefined;
  const wanted = new Set(keys.map((key) => normalizeLookupKey(key)));
  for (const [key, value] of Object.entries(record)) {
    if (wanted.has(normalizeLookupKey(key))) {
      return value;
    }
  }
  return undefined;
}

function pickStringIgnoreCase(
  records: Array<Record<string, unknown> | null | undefined>,
  keys: string[],
): string {
  for (const record of records) {
    const value = pickFieldIgnoreCase(record, keys);
    const asString = toStringValue(value) || coerceToDisplayString(value);
    if (asString) return asString;
  }
  return "";
}

function pickObjectIgnoreCase(
  record: Record<string, unknown> | null | undefined,
  keys: string[],
): Record<string, unknown> | null {
  return asObjectRecord(pickFieldIgnoreCase(record, keys));
}

function mapDocumentChecklistArray(candidate: unknown[]): CollegeAdminDocumentItemOut[] {
  return candidate
    .map((item) => normalizeCollegeAdminDocumentItem(item))
    .filter((item): item is CollegeAdminDocumentItemOut => Boolean(item));
}

/** True when a normalized key looks like a document-checklist collection. */
function isDocumentChecklistCollectionKey(normalizedKey: string): boolean {
  return (
    normalizedKey === "items"
    || normalizedKey === "documents"
    || normalizedKey === "documentchecklist"
    || normalizedKey === "checklist"
    || normalizedKey === "documentlist"
    || normalizedKey === "documentitems"
    || normalizedKey === "documentrequirements"
    || normalizedKey === "checklistitems"
    || normalizedKey === "docs"
  );
}

function normalizeCollegeAdminDocumentItem(raw: unknown): CollegeAdminDocumentItemOut | null {
  if (typeof raw === "string") {
    const name = raw.trim();
    if (!name) return null;
    return { name, required: true };
  }

  const root = asObjectRecord(raw);
  if (!root) return null;

  const nested =
    pickObjectIgnoreCase(root, ["document", "item", "data"])
    ?? root;

  const name = pickStringIgnoreCase([nested, root], [
    "name",
    "documentName",
    "document_name",
    "document",
    "docName",
    "doc_name",
    "title",
    "label",
    "value",
    "text",
    "fileName",
    "filename",
    "file_name",
  ]);
  if (!name) return null;

  const requiredRaw = pickFieldIgnoreCase(nested, [
    "required",
    "isRequired",
    "is_required",
    "mandatory",
  ]) ?? pickFieldIgnoreCase(root, [
    "required",
    "isRequired",
    "is_required",
    "mandatory",
  ]);

  return {
    name,
    required: requiredRaw === undefined || requiredRaw === null ? true : toBoolean(requiredRaw),
  };
}

/**
 * Deep-scan for arrays that look like document checklist rows.
 * Handles nested/PascalCase wrappers the preferred-key list may miss.
 */
function deepFindDocumentChecklistArrays(
  value: unknown,
  depth = 0,
  found: unknown[][] = [],
  seen: Set<unknown> = new Set(),
): unknown[][] {
  if (depth > 6 || value == null || typeof value !== "object") {
    return found;
  }
  if (seen.has(value)) return found;
  seen.add(value);

  if (Array.isArray(value)) {
    if (mapDocumentChecklistArray(value).length > 0) {
      found.push(value);
    }
    for (const item of value) {
      deepFindDocumentChecklistArrays(item, depth + 1, found, seen);
    }
    return found;
  }

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (Array.isArray(child) && isDocumentChecklistCollectionKey(normalizeLookupKey(key))) {
      found.push(child);
    }
    deepFindDocumentChecklistArrays(child, depth + 1, found, seen);
  }
  return found;
}

/**
 * Collect document-checklist array candidates from a response object.
 * Prefers known keys, then any array whose key normalizes to a checklist collection name.
 */
function collectDocumentChecklistArrayCandidates(root: Record<string, unknown>): unknown[][] {
  const dataRecord =
    pickObjectIgnoreCase(root, ["data", "Data"])
    ?? null;
  const resultRecord =
    pickObjectIgnoreCase(root, ["result", "Result"])
    ?? null;
  const payloadRecord =
    pickObjectIgnoreCase(root, ["payload", "body", "Payload", "Body"])
    ?? null;
  const checklistRecord =
    pickObjectIgnoreCase(root, ["documentChecklist", "document_checklist", "checklist"])
    ?? pickObjectIgnoreCase(dataRecord, ["documentChecklist", "document_checklist", "checklist"])
    ?? pickObjectIgnoreCase(resultRecord, ["documentChecklist", "document_checklist", "checklist"])
    ?? pickObjectIgnoreCase(payloadRecord, ["documentChecklist", "document_checklist", "checklist"]);

  const preferredKeys = [
    "items",
    "documents",
    "documentChecklist",
    "document_checklist",
    "checklist",
    "documentList",
    "document_list",
    "documentItems",
    "document_items",
    "documentRequirements",
    "document_requirements",
    "docs",
    "content",
  ];

  const candidates: unknown[][] = [];
  const seen = new Set<unknown>();
  const pushArray = (value: unknown) => {
    if (!Array.isArray(value) || seen.has(value)) return;
    seen.add(value);
    candidates.push(value);
  };

  for (const record of [root, dataRecord, resultRecord, payloadRecord, checklistRecord]) {
    if (!record) continue;
    for (const key of preferredKeys) {
      pushArray(pickFieldIgnoreCase(record, [key]));
    }
    for (const [key, value] of Object.entries(record)) {
      if (!isDocumentChecklistCollectionKey(normalizeLookupKey(key))) continue;
      pushArray(value);
    }
  }

  // Generic wrappers last.
  pushArray(pickFieldIgnoreCase(root, ["data"]));
  pushArray(pickFieldIgnoreCase(root, ["results", "result"]));
  pushArray(pickFieldIgnoreCase(root, ["payload", "body"]));

  return candidates;
}

function parseCollegeAdminDocumentChecklistResponse(raw: unknown): CollegeAdminDocumentItemOut[] {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      return parseCollegeAdminDocumentChecklistResponse(JSON.parse(trimmed) as unknown);
    } catch {
      // Plain status text (e.g. "OK") is not a checklist payload.
      return [];
    }
  }

  if (Array.isArray(raw)) {
    return mapDocumentChecklistArray(raw);
  }

  const root = asObjectRecord(raw);
  if (!root) {
    // Non-object success bodies (boolean/number) are not checklist payloads.
    return [];
  }

  const candidates = collectDocumentChecklistArrayCandidates(root);

  // Prefer the first non-empty mapped list. Empty arrays (e.g. content: []) must not
  // short-circuit before a later nested documents array is considered.
  let emptyResult: CollegeAdminDocumentItemOut[] | null = null;
  for (const candidate of candidates) {
    const mapped = mapDocumentChecklistArray(candidate);
    if (mapped.length > 0) {
      return mapped;
    }
    if (emptyResult === null) {
      emptyResult = mapped;
    }
  }

  // Last resort: deep scan for document-like rows anywhere in the payload.
  for (const candidate of deepFindDocumentChecklistArrays(root)) {
    const mapped = mapDocumentChecklistArray(candidate);
    if (mapped.length > 0) {
      return mapped;
    }
  }

  const single = normalizeCollegeAdminDocumentItem(raw);
  if (single) return [single];

  return emptyResult ?? [];
}

/**
 * Lists circulars for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/circulars
 */
export async function fetchCollegeAdminCirculars(): Promise<CollegeAdminCircularOut[]> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/circulars", {
      headers: {
        Accept: "application/json",
      },
    });
    return parseCollegeAdminCircularsResponse(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load circulars."));
  }
}

/**
 * Creates a circular for the signed-in College Admin.
 * Uploads the PDF via POST /api/v1/files (prefix=circulars), then
 * POST /api/v1/spot/college-admin/circulars with JSON { title, date, fileId }.
 */
export async function createCollegeAdminCircular(
  payload: CreateCollegeAdminCircularPayload,
): Promise<CollegeAdminCircularOut | void> {
  try {
    const uploaded = await uploadFile(payload.file, "circulars");
    const body = {
      title: payload.title,
      date: payload.date,
      fileId: uploaded.id,
    };
    const response = await httpClient.post<unknown>("/api/v1/spot/college-admin/circulars", body, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.data == null || response.data === "") {
      return;
    }
    const created = normalizeCollegeAdminCircular(response.data);
    if (created) return created;
    const parsed = parseCollegeAdminCircularsResponse(response.data);
    return parsed[0];
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to create circular."));
  }
}

/**
 * Updates a circular for the signed-in College Admin.
 * When a new PDF is provided, uploads it via POST /api/v1/files first.
 * PUT /api/v1/spot/college-admin/circulars/{assetId} with JSON only.
 */
export async function updateCollegeAdminCircular(
  assetId: number | string,
  payload: UpdateCollegeAdminCircularPayload,
): Promise<CollegeAdminCircularOut | void> {
  try {
    const body: { title: string; date: string; fileId?: number } = {
      title: payload.title,
      date: payload.date,
    };
    if (payload.file) {
      const uploaded = await uploadFile(payload.file, "circulars");
      body.fileId = uploaded.id;
    }
    const response = await httpClient.put<unknown>(
      `/api/v1/spot/college-admin/circulars/${encodeURIComponent(String(assetId))}`,
      body,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    if (response.data == null || response.data === "") {
      return;
    }
    const updated = normalizeCollegeAdminCircular(response.data);
    if (updated) return updated;
    const parsed = parseCollegeAdminCircularsResponse(response.data);
    return parsed[0];
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update circular."));
  }
}

/**
 * Deletes a circular for the signed-in College Admin.
 * DELETE /api/v1/spot/college-admin/circulars/{assetId}
 */
export async function deleteCollegeAdminCircular(assetId: number | string): Promise<void> {
  try {
    await httpClient.delete<unknown>(
      `/api/v1/spot/college-admin/circulars/${encodeURIComponent(String(assetId))}`,
      {
        headers: {
          Accept: "application/json",
        },
      },
    );
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to delete circular."));
  }
}

/**
 * Returns bank details for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/bank-details
 */
export async function fetchCollegeAdminBankDetails(): Promise<CollegeAdminBankDetailsOut> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/bank-details", {
      headers: {
        Accept: "application/json",
      },
    });
    return normalizeCollegeAdminBankDetails(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load bank details."));
  }
}

/**
 * Updates bank details for the signed-in College Admin.
 * PUT /api/v1/spot/college-admin/bank-details
 */
export async function updateCollegeAdminBankDetails(
  payload: UpdateCollegeAdminBankDetailsPayload,
): Promise<CollegeAdminBankDetailsOut | void> {
  try {
    const body = toCollegeAdminBankDetailsRequestBody(payload);
    const response = await httpClient.put<unknown>("/api/v1/spot/college-admin/bank-details", body, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    if (response.data == null || response.data === "") {
      return;
    }
    return normalizeCollegeAdminBankDetails(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update bank details."));
  }
}

/**
 * Returns the document checklist for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/document-checklist
 */
export async function fetchCollegeAdminDocumentChecklist(options?: {
  refreshed?: boolean;
}): Promise<CollegeAdminDocumentItemOut[]> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/document-checklist", {
      headers: {
        Accept: "application/json",
      },
    });
    console.log(
      options?.refreshed
        ? "[DocumentChecklist] Refreshed GET response"
        : "[DocumentChecklist] GET response",
      response.data,
    );
    const parsed = parseCollegeAdminDocumentChecklistResponse(response.data);
    return parsed;
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load document checklist."));
  }
}

/**
 * Replaces the document checklist for the signed-in College Admin.
 * PUT /api/v1/spot/college-admin/document-checklist
 */
export async function updateCollegeAdminDocumentChecklist(
  payload: UpdateCollegeAdminDocumentChecklistPayload,
): Promise<void> {
  try {
    const body = toCollegeAdminDocumentChecklistRequestBody(payload);
    console.log("[DocumentChecklist] PUT request body", body);
    const response = await httpClient.put<unknown>(
      "/api/v1/spot/college-admin/document-checklist",
      body,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    console.log("[DocumentChecklist] PUT response", response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update document checklist."));
  }
}

/** Normalized college logo from College Admin logo APIs. */
export interface CollegeAdminLogoOut {
  /** Displayable image URL (direct or signed download URL). */
  logoUrl?: string;
  /** Stored file id when the API returns asset metadata instead of a URL. */
  fileId?: number;
}

interface CollegeAdminLogoMapped {
  logoUrl?: string;
  fileId?: number;
}

function resolveAbsoluteMediaUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
    return trimmed;
  }
  const baseURL = String(httpClient.defaults.baseURL ?? "").replace(/\/$/, "");
  if (!baseURL) return trimmed;
  return `${baseURL}${trimmed.startsWith("/") ? "" : "/"}${trimmed}`;
}

function normalizeCollegeAdminLogo(raw: unknown): CollegeAdminLogoMapped {
  if (raw == null || raw === "") {
    return {};
  }

  if (typeof raw === "string") {
    const logoUrl = raw.trim();
    return logoUrl ? { logoUrl } : {};
  }

  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { fileId: raw };
  }

  const root = asRecord(raw) ?? {};
  const nested =
    asRecord(root.data)
    ?? asRecord(root.result)
    ?? asRecord(root.logo)
    ?? asRecord(root.file)
    ?? asRecord(root.asset)
    ?? asRecord(root.college);
  const candidates = [root, nested].filter(
    (item): item is Record<string, unknown> => Boolean(item),
  );

  // Prefer explicit download / signed URL fields over generic `logo` (which may be a path or id).
  const logoUrl = pickStringFromRecords(candidates, [
    "logoUrl",
    "logo_url",
    "downloadUrl",
    "download_url",
    "signedUrl",
    "signed_url",
    "imageUrl",
    "image_url",
    "url",
    "logo",
  ]);

  const fileId =
    (() => {
      for (const record of candidates) {
        const id =
          toNumber(record.fileId)
          ?? toNumber(record.file_id)
          ?? toNumber(record.logoFileId)
          ?? toNumber(record.logo_file_id)
          // `{ logo: 123 }` — numeric logo field is a file id, not a URL
          ?? (typeof record.logo === "number" || (typeof record.logo === "string" && /^\d+$/.test(record.logo.trim()))
            ? toNumber(record.logo)
            : null);
        if (id != null) return id;
      }
      return null;
    })();

  // If a URL-ish string field was actually a bare numeric id, treat it as fileId.
  const numericLogoAsFileId =
    !logoUrl || /^https?:\/\//i.test(logoUrl) || logoUrl.startsWith("/") || logoUrl.startsWith("data:")
      ? null
      : toNumber(logoUrl);

  // Prefer fileId when the only "logoUrl" candidate was a numeric id string.
  const resolvedFileId = fileId ?? numericLogoAsFileId ?? undefined;
  const resolvedLogoUrl =
    logoUrl && numericLogoAsFileId == null && !(fileId != null && logoUrl === String(fileId))
      ? logoUrl
      : undefined;

  return {
    ...(resolvedLogoUrl ? { logoUrl: resolvedLogoUrl } : {}),
    ...(resolvedFileId != null ? { fileId: resolvedFileId } : {}),
  };
}

/**
 * Resolve a displayable logo URL from a mapped GET/PUT logo payload.
 * Contract:
 * - If a URL is present → use it (resolve relative against API base).
 * - Else if fileId is present → GET /api/v1/files/{fileId}/download-url and use signed `url`.
 */
async function resolveCollegeAdminLogoDisplayUrl(
  mapped: CollegeAdminLogoMapped,
  options?: { authorization?: string },
): Promise<CollegeAdminLogoOut> {
  if (mapped.logoUrl?.trim()) {
    const logoUrl = resolveAbsoluteMediaUrl(mapped.logoUrl);
    console.log("[college-logo] Mapped logo data", { ...mapped, resolvedLogoUrl: logoUrl });
    console.log("[college-logo] Final image URL for <img>", logoUrl);
    return { logoUrl, ...(mapped.fileId != null ? { fileId: mapped.fileId } : {}) };
  }

  if (mapped.fileId != null) {
    console.log("[college-logo] Mapped logo data has fileId only — fetching download-url", {
      fileId: mapped.fileId,
    });
    const downloadUrlResponse = await fetchFileDownloadUrl(mapped.fileId, {
      authorization: options?.authorization,
    });
    // TEMP DEBUG
    console.log("[SpotRound] download-url API response", {
      logoFileId: mapped.fileId,
      ...downloadUrlResponse,
    });
    const logoUrl = resolveAbsoluteMediaUrl(downloadUrlResponse.url);
    console.log("[college-logo] Signed download-url resolved for logo", {
      fileId: mapped.fileId,
      rawSignedUrl: downloadUrlResponse.url,
      logoUrl,
      expiresAt: downloadUrlResponse.expiresAt,
    });
    console.log("[SpotRound] resolved logoUrl", logoUrl || null);
    console.log("[college-logo] Final image URL for <img>", logoUrl);
    return { logoUrl, fileId: mapped.fileId };
  }

  console.log("[college-logo] Mapped logo data has no URL or fileId — placeholder will show", mapped);
  return {};
}

/**
 * Returns the college logo for the signed-in College Admin.
 * GET /api/v1/spot/college-admin/logo
 * Missing logo (404 / empty) returns `{}` so callers keep placeholder behavior.
 *
 * When the API returns file metadata (`fileId`) instead of a ready URL, resolves a
 * temporary signed download URL via GET /api/v1/files/{fileId}/download-url.
 */
export async function fetchCollegeAdminLogo(): Promise<CollegeAdminLogoOut> {
  try {
    const response = await httpClient.get<unknown>("/api/v1/spot/college-admin/logo", {
      headers: {
        Accept: "application/json",
      },
    });
    console.log("[college-logo] GET /api/v1/spot/college-admin/logo response", {
      status: response.status,
      data: response.data,
    });
    if (response.data == null || response.data === "") {
      console.log("[college-logo] Empty GET logo body — placeholder will show");
      return {};
    }
    const mapped = normalizeCollegeAdminLogo(response.data);
    console.log("[college-logo] Mapped logo data (raw normalize)", mapped);
    return await resolveCollegeAdminLogoDisplayUrl(mapped);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("[college-logo] GET logo returned 404 — placeholder will show");
      return {};
    }
    console.error("[college-logo] GET /api/v1/spot/college-admin/logo failed", error);
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load college logo."));
  }
}

/**
 * Returns the college logo for a Viewer (public) college board.
 * GET /api/v1/spot/viewer/colleges/{collegeId}/logo
 * Missing logo (404 / empty) returns `{}` so callers keep placeholder behavior.
 */
export async function fetchViewerCollegeLogo(
  collegeId: number | string,
): Promise<CollegeAdminLogoOut> {
  const url = `/api/v1/spot/viewer/colleges/${encodeURIComponent(String(collegeId))}/logo`;
  const authorization = spotViewerAuthorizationHeader();

  try {
    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: authorization,
      },
    });
    // TEMP DEBUG
    console.log("[SpotRound] viewer logo API response", {
      collegeId: String(collegeId),
      status: response.status,
      data: response.data,
    });
    if (response.data == null || response.data === "") {
      console.log("[SpotRound] viewer logo empty — placeholder will show", { collegeId: String(collegeId) });
      return {};
    }
    const mapped = normalizeCollegeAdminLogo(response.data);
    console.log("[SpotRound] viewer logo mapped", {
      collegeId: String(collegeId),
      mapped,
    });
    return await resolveCollegeAdminLogoDisplayUrl(mapped, { authorization });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      console.log("[SpotRound] viewer logo 404 — placeholder will show", { collegeId: String(collegeId) });
      return {};
    }
    console.error("[SpotRound] GET viewer college logo failed", {
      collegeId: String(collegeId),
      url,
      error,
    });
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load college logo."));
  }
}

/**
 * Uploads / replaces the college logo for the signed-in College Admin.
 * Uploads via POST /api/v1/files (prefix=logos), then
 * PUT /api/v1/spot/college-admin/logo with JSON { fileId }.
 */
export async function updateCollegeAdminLogo(
  file: File,
): Promise<CollegeAdminLogoOut | void> {
  try {
    // Step 1: multipart upload → fileId
    const uploaded = await uploadFile(file, "logos");
    const putBody = { fileId: uploaded.id };
    const putHeaders = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    console.info("[college-logo] Step 1 complete — attaching fileId", {
      fileId: uploaded.id,
      fileIdType: typeof uploaded.id,
      putBody,
      putHeaders,
    });

    // Step 2: JSON attach logo to college
    const response = await httpClient.put<unknown>(
      "/api/v1/spot/college-admin/logo",
      putBody,
      {
        headers: putHeaders,
      },
    );

    console.info("[college-logo] PUT /api/v1/spot/college-admin/logo succeeded", {
      status: response.status,
      putBody,
      putResponse: response.data,
      responseHeaders: response.headers,
    });

    if (response.data == null || response.data === "") {
      return;
    }
    const mapped = normalizeCollegeAdminLogo(response.data);
    console.log("[college-logo] Mapped PUT logo data (raw normalize)", mapped);
    return await resolveCollegeAdminLogoDisplayUrl(mapped);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("[college-logo] Logo upload flow failed", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        requestData: error.config?.data,
        requestHeaders: {
          Accept: error.config?.headers?.Accept ?? error.config?.headers?.accept,
          "Content-Type":
            error.config?.headers?.["Content-Type"]
            ?? error.config?.headers?.["content-type"],
          Authorization: error.config?.headers?.Authorization
            ? "(present, redacted)"
            : "(missing)",
        },
        responseData: error.response?.data,
      });
    } else {
      console.error("[college-logo] Logo upload flow failed (non-HTTP)", error);
    }
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to update college logo."));
  }
}

/**
 * Lists circulars for a college on the Viewer (public) board.
 * GET /api/v1/spot/viewer/colleges/{collegeId}/circulars
 */
export async function fetchViewerCollegeCirculars(
  collegeId: number | string,
): Promise<CollegeAdminCircularOut[]> {
  const url = `/api/v1/spot/viewer/colleges/${encodeURIComponent(String(collegeId))}/circulars`;
  console.info("[ViewerAPI] Request URL", { url, collegeId: String(collegeId) });
  try {
    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: spotViewerAuthorizationHeader(),
      },
    });
    // TEMP DEBUG — public circular download
    console.log("[CircularDownload] Raw API response", response.data);
    const rawRows = Array.isArray(response.data)
      ? response.data
      : response.data && typeof response.data === "object"
        ? [response.data]
        : [];
    console.log(
      "[CircularDownload] Raw circular field inventory",
      rawRows.map((row, index) => {
        const record =
          row && typeof row === "object" && !Array.isArray(row)
            ? (row as Record<string, unknown>)
            : {};
        return {
          index,
          keys: Object.keys(record),
          fileId: record.fileId ?? record.file_id,
          assetId: record.assetId ?? record.asset_id,
          documentId: record.documentId ?? record.document_id,
          file: record.file,
          url: record.url,
          downloadUrl: record.downloadUrl ?? record.download_url,
          filename: record.filename ?? record.fileName ?? record.file_name,
        };
      }),
    );
    const parsed = parseCollegeAdminCircularsResponse(response.data);
    console.log("[CircularDownload] Parsed / mapped API circulars", parsed);
    return parsed;
  } catch (error) {
    console.error("[CircularDownload] Circulars GET request/response error", error);
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load circulars."));
  }
}

/**
 * Downloads a circular PDF for the Viewer (public) board via fileId.
 * Uses signed-URL flow: GET /api/v1/files/{fileId}/download-url → fetch signed url (no auth).
 */
export async function downloadViewerCircularFile(
  fileId: number | string,
  filename?: string,
): Promise<void> {
  await downloadFile(fileId, {
    filename,
    authorization: spotViewerAuthorizationHeader(),
  });
}

/**
 * Downloads a circular PDF for the signed-in College Admin via fileId.
 * Uses the same signed-URL flow; auth comes from the HTTP client (Bearer / Basic / impersonation).
 */
export async function downloadCollegeAdminCircularFile(
  fileId: number | string,
  filename?: string,
): Promise<void> {
  await downloadFile(fileId, { filename });
}

/** @deprecated Use fetchViewerCollegeCirculars */
export const fetchPublicCollegeCirculars = fetchViewerCollegeCirculars;

/**
 * Returns bank details for a college on the Viewer (public) board.
 * GET /api/v1/spot/viewer/colleges/{collegeId}/bank-details
 */
export async function fetchViewerCollegeBankDetails(
  collegeId: number | string,
): Promise<CollegeAdminBankDetailsOut> {
  const url = `/api/v1/spot/viewer/colleges/${encodeURIComponent(String(collegeId))}/bank-details`;
  console.info("[ViewerAPI] Request URL", { url, collegeId: String(collegeId) });
  try {
    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: spotViewerAuthorizationHeader(),
      },
    });
    return normalizeCollegeAdminBankDetails(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load bank details."));
  }
}

/** @deprecated Use fetchViewerCollegeBankDetails */
export const fetchPublicCollegeBankDetails = fetchViewerCollegeBankDetails;

/**
 * Returns the document checklist for a college on the Viewer (public) board.
 * GET /api/v1/spot/viewer/colleges/{collegeId}/document-checklist
 */
export async function fetchViewerCollegeDocumentChecklist(
  collegeId: number | string,
): Promise<CollegeAdminDocumentItemOut[]> {
  const url =
    `/api/v1/spot/viewer/colleges/${encodeURIComponent(String(collegeId))}/document-checklist`;
  console.info("[ViewerAPI] Request URL", { url, collegeId: String(collegeId) });
  try {
    const response = await httpClient.get<unknown>(url, {
      headers: {
        Accept: "application/json",
        Authorization: spotViewerAuthorizationHeader(),
      },
    });
    return parseCollegeAdminDocumentChecklistResponse(response.data);
  } catch (error) {
    throw new Error(getCollegeAdminRoundsErrorMessage(error, "Failed to load document checklist."));
  }
}

/** @deprecated Use fetchViewerCollegeDocumentChecklist */
export const fetchPublicCollegeDocumentChecklist = fetchViewerCollegeDocumentChecklist;
